import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { invalidateChantier, invalidateChantierDateFin } from '@/lib/queryInvalidation';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ChantierMetrics } from '@/hooks/useChantierMetrics';
import { useDelayPenaltySimulation, TieredPenalty, SelectedMemberForDelay } from '@/hooks/useDelayPenaltySimulation';

import { PenaltyComparison } from './PenaltyComparison';
import { ModalSelectionEquipeRetard } from './ModalSelectionEquipeRetard';
import { TipsModal } from '@/components/tips/TipsModal';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { format, addBusinessDays, addDays, eachDayOfInterval, isWeekend, isSameDay, parse } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { AlertTriangle, Save, FileText, Users, Info, Beaker, Pencil, Trash2, Loader2, CalendarDays, Lock, Check, RefreshCw, Clock, Calendar as CalendarIcon, User } from 'lucide-react';
import { pdf } from '@react-pdf/renderer';
import { RapportRetardPDF, type RapportRetardData } from '@/components/pdf/RapportRetardPDF';
import { cn } from '@/lib/utils';
import { EquipeIcon } from '@/components/equipe/EquipeIcon';

// Clé localStorage pour le tips "vu"
const TIPS_RETARD_SEEN_KEY = 'b8ild_tips_retard_seen';

// Convertir selectedWorkDays (Map) en format JSONB pour stockage
// Format: [{ "date": "2026-01-03", "period": "AM", "value": 0.5 }, ...]
const convertWorkDaysToJsonb = (workDays: Map<string, number>): Array<{ date: string; period: string; value: number }> => {
  const result: Array<{ date: string; period: string; value: number }> = [];
  workDays.forEach((value, key) => {
    const [date, period] = key.split(':');
    result.push({ date, period, value });
  });
  // Trier par date puis par période (AM avant PM)
  return result.sort((a, b) => {
    if (a.date !== b.date) return a.date.localeCompare(b.date);
    return a.period === 'AM' ? -1 : 1;
  });
};

// Clé sessionStorage pour persister les données du simulateur (par chantier)
const getSessionStorageKey = (chantierId: string) => `b8ild_delay_simulator_${chantierId}`;

// Interface pour les données persistées
interface PersistedDelayData {
  delayDays: number;
  penaltyType: 'percentage' | 'fixed' | 'tiered';
  penaltyValue: number;
  penaltyEnabled: boolean;
  selectedMembersForDelay: SelectedMemberForDelay[];
  teamChoice: 'current' | 'custom' | null;
  selectedEndDate: string | null; // ISO string
  tieredRates: TieredPenalty[];
}

// Composant Section Point Critique (mode simulation) ou Bilan du Retard (mode post-enregistrement)
interface PointCritiqueSectionProps {
  joursSimules: number;
  pointCritique: number;
  // Props pour le mode "Bilan du Retard" (post-enregistrement)
  isDelayRegistered?: boolean;
  coutTotalRetard?: number;
  margeFinaleAvecRetard?: number;
  rentabiliteAvecRetard?: number;
  joursRestantsAvantNouveauDeficit?: number;
}

function PointCritiqueSection({
  joursSimules,
  pointCritique,
  isDelayRegistered = false,
  coutTotalRetard = 0,
  margeFinaleAvecRetard = 0,
  rentabiliteAvecRetard = 0,
  joursRestantsAvantNouveauDeficit = 0,
}: PointCritiqueSectionProps) {

  // MODE BILAN DU RETARD (après enregistrement)
  if (isDelayRegistered) {
    const isRentable = rentabiliteAvecRetard >= 0;
    const isHealthy = rentabiliteAvecRetard >= 10;

    return (
      <Card className="border-green-500/30 bg-green-500/5">
        <CardHeader className="pb-4">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Check className="h-4 w-4 text-green-500" />
            Bilan du Retard
          </CardTitle>
          <CardDescription>
            Impact financier du retard enregistré
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Métriques du bilan */}
          <div className="grid grid-cols-3 gap-4">
            {/* Coût total du retard */}
            <div className="text-center">
              <p className="text-xs text-muted-foreground mb-1">Coût du retard</p>
              <p className="text-xl font-bold font-mono text-orange-500">
                {coutTotalRetard.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €
              </p>
              <p className="text-xs text-muted-foreground">{joursSimules} jour{joursSimules > 1 ? 's' : ''}</p>
            </div>

            {/* Nouvelle rentabilité */}
            <div className="text-center">
              <p className="text-xs text-muted-foreground mb-1">Rentabilité</p>
              <p className={cn(
                "text-xl font-bold font-mono",
                isHealthy ? "text-success" : isRentable ? "text-warning" : "text-danger"
              )}>
                {rentabiliteAvecRetard.toFixed(1)}%
              </p>
              <Badge
                variant="outline"
                className={cn(
                  "text-[10px] font-semibold mt-1",
                  isHealthy && "border-success/50 bg-success/10 text-success",
                  isRentable && !isHealthy && "border-warning/50 bg-warning/10 text-warning",
                  !isRentable && "border-danger/50 bg-danger/10 text-danger"
                )}
              >
                {isHealthy ? 'Saine' : isRentable ? 'Faible' : 'Déficitaire'}
              </Badge>
            </div>

            {/* Marge de manœuvre */}
            <div className="text-center">
              <p className="text-xs text-muted-foreground mb-1">Marge restante</p>
              <p className={cn(
                "text-xl font-bold font-mono",
                joursRestantsAvantNouveauDeficit > 5 ? "text-success" :
                joursRestantsAvantNouveauDeficit > 2 ? "text-warning" : "text-danger"
              )}>
                {joursRestantsAvantNouveauDeficit > 0 ? joursRestantsAvantNouveauDeficit : 0}
              </p>
              <p className="text-xs text-muted-foreground">jours avant déficit</p>
            </div>
          </div>

          {/* Message de synthèse */}
          <div className={cn(
            "p-3 rounded-lg text-sm text-center",
            isHealthy ? "bg-success/10 text-success" :
            isRentable ? "bg-warning/10 text-warning" : "bg-danger/10 text-danger"
          )}>
            {isHealthy ? (
              <>✅ Le chantier reste rentable malgré le retard</>
            ) : isRentable ? (
              <>⚠️ Rentabilité réduite - Surveillez l'avancement</>
            ) : (
              <>🚨 Le chantier est désormais déficitaire</>
            )}
          </div>

          {/* Nouvelle marge finale */}
          <div className="flex justify-between items-center pt-2 border-t">
            <span className="text-sm text-muted-foreground">Nouvelle marge finale</span>
            <span className={cn(
              "text-lg font-bold font-mono",
              margeFinaleAvecRetard >= 0 ? "text-success" : "text-danger"
            )}>
              {margeFinaleAvecRetard.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €
            </span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // MODE SIMULATION (avant enregistrement)
  // Ne pas afficher si le point critique est >= 100 (chantier très rentable)
  if (pointCritique >= 100) return null;

  const isOver = joursSimules >= pointCritique;
  const isClose = !isOver && joursSimules >= pointCritique - 2;
  const joursRestants = pointCritique - joursSimules;
  const pourcentage = Math.min((joursSimules / pointCritique) * 100, 100);

  // Utilisation des couleurs du design system B8ild
  const status = isOver
    ? { label: 'Déficitaire', color: 'text-danger', bgBar: 'bg-danger' }
    : isClose
      ? { label: 'Critique', color: 'text-warning', bgBar: 'bg-warning' }
      : { label: 'Rentable', color: 'text-success', bgBar: 'bg-success' };

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          Point Critique
        </CardTitle>
        <CardDescription>
          Seuil de retard avant déficit
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Métriques principales */}
        <div className="grid grid-cols-3 gap-4">
          {/* Seuil */}
          <div className="text-center">
            <p className="text-xs text-muted-foreground mb-1">Seuil</p>
            <p className="text-2xl font-bold font-mono text-foreground">{pointCritique}</p>
            <p className="text-xs text-muted-foreground">jours</p>
          </div>

          {/* Statut */}
          <div className="text-center">
            <p className="text-xs text-muted-foreground mb-1">Statut</p>
            <Badge
              variant="outline"
              className={cn(
                "text-xs font-semibold",
                isOver && "border-danger/50 bg-danger/10 text-danger",
                isClose && !isOver && "border-warning/50 bg-warning/10 text-warning",
                !isOver && !isClose && "border-success/50 bg-success/10 text-success"
              )}
            >
              {status.label}
            </Badge>
          </div>

          {/* Retard simulé */}
          <div className="text-center">
            <p className="text-xs text-muted-foreground mb-1">Simulé</p>
            <p className={cn("text-2xl font-bold font-mono", status.color)}>{joursSimules}</p>
            <p className="text-xs text-muted-foreground">jours</p>
          </div>
        </div>

        {/* Barre de progression */}
        <div className="space-y-2">
          <div className="relative h-2 bg-muted rounded-full overflow-hidden">
            <div
              className={cn("h-full rounded-full transition-all duration-300", status.bgBar)}
              style={{ width: `${pourcentage}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>0</span>
            <span className="font-medium">{pointCritique} jours</span>
          </div>
        </div>

        {/* Message */}
        <p className="text-sm text-center text-muted-foreground">
          {isOver ? (
            <>Dépassement de <span className="font-semibold text-danger">{joursSimules - pointCritique} jour{joursSimules - pointCritique > 1 ? 's' : ''}</span></>
          ) : (
            <>Marge restante : <span className={cn("font-semibold", status.color)}>{joursRestants} jour{joursRestants > 1 ? 's' : ''}</span></>
          )}
        </p>
      </CardContent>
    </Card>
  );
}

interface DelayPenaltySimulatorProps {
  chantierId: string;
  metrics: ChantierMetrics | null;
  entrepriseId?: string;
  onSavePenalty?: (delayDays: number) => void;
  nomChantier?: string;
  client?: string;
  onCriticalDelayDaysChange?: (days: number) => void; // Callback pour remonter criticalDelayDays au parent
  openEditMode?: boolean; // Si true, ouvre directement le mode édition (depuis clic sur badge retard)
}

export default function DelayPenaltySimulator({ chantierId, metrics, entrepriseId, onSavePenalty, nomChantier, client, onCriticalDelayDaysChange, openEditMode }: DelayPenaltySimulatorProps) {
  const { user } = useAuth();
  const location = useLocation();
  const queryClient = useQueryClient();
  // Date de fin réelle choisie par l'utilisateur
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(null);
  // Nombre de jours ouvrés travaillés (0.5 en 0.5) - défaut 0.5 jour
  const [delayDays, setDelayDays] = useState(0.5);
  const [penaltyType, setPenaltyType] = useState<'percentage' | 'fixed' | 'tiered'>('percentage');
  const [penaltyValue, setPenaltyValue] = useState(0.5);
  const [saving, setSaving] = useState(false);
  const [penaltyEnabled, setPenaltyEnabled] = useState(true);
  // États pour la sélection d'équipe pendant le retard
  const [modalEquipeOpen, setModalEquipeOpen] = useState(false);
  const [selectedMembersForDelay, setSelectedMembersForDelay] = useState<SelectedMemberForDelay[]>([]);
  // État pour le choix d'équipe : null = pas encore choisi, 'current' = équipe actuelle, 'custom' = équipe personnalisée
  const [teamChoice, setTeamChoice] = useState<'current' | 'custom' | null>(null);
  // État pour la modal de confirmation avant enregistrement
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  // État pour le tips contextuel
  const [showTips, setShowTips] = useState(false);
  // État pour le clignotement du bouton "Appliquer les pénalités"
  const [isBlinking, setIsBlinking] = useState(false);
  // État pour le clignotement du slider des jours
  const [isSliderBlinking, setIsSliderBlinking] = useState(false);
  // État pour le clignotement des boutons équipe
  const [isTeamButtonsBlinking, setIsTeamButtonsBlinking] = useState(false);
  // Flag pour savoir si on vient de la checklist
  const [fromChecklistFlag, setFromChecklistFlag] = useState(false);
  // État pour le retard enregistré existant
  const [existingDelay, setExistingDelay] = useState<{
    id: string;
    montant: number;
    description: string;
    date_frais: string;
  } | null>(null);
  // Coût réel du retard calculé depuis les affectations (pour affichage dans le banner)
  const [delayCostFromAffectations, setDelayCostFromAffectations] = useState<number>(0);
  // Coût journalier de l'équipe mobilisée pour le retard (pour calcul jours restants post-retard)
  const [coutJournalierEquipeRetard, setCoutJournalierEquipeRetard] = useState<number>(0);
  // Métriques initiales sauvegardées au premier chargement (pour Point Critique)
  const initialMetricsRef = useRef<ChantierMetrics | null>(null);
  // État pour la suppression en cours
  const [deleting, setDeleting] = useState(false);
  // État pour la modal de confirmation de suppression
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  // État pour la génération du rapport PDF
  const [generatingPdf, setGeneratingPdf] = useState(false);
  // État pour la modal de sélection des jours travaillés (TOUJOURS affiché avant enregistrement)
  const [showDaysSelectionModal, setShowDaysSelectionModal] = useState(false);
  // Jours sélectionnés comme travaillés dans la période de retard
  // Map: clé "YYYY-MM-DD:AM" ou "YYYY-MM-DD:PM" -> valeur 0.5
  const [selectedWorkDays, setSelectedWorkDays] = useState<Map<string, number>>(new Map());
  // Dates réelles extraites des jours sélectionnés (premier et dernier jour travaillé)
  const [realStartDate, setRealStartDate] = useState<Date | null>(null);
  const [realEndDate, setRealEndDate] = useState<Date | null>(null);
  // Flag indiquant qu'on veut enregistrer après confirmation des jours
  const [pendingSave, setPendingSave] = useState(false);
  // État pour le mode édition : false = figé (lecture seule), true = éditable
  const [isEditMode, setIsEditMode] = useState(true);

  // Flag pour éviter de recharger après un enregistrement local
  const [justSaved, setJustSaved] = useState(false);
  // Flag indiquant que l'utilisateur a déjà confirmé sa sélection de jours (via Modal 1)
  const [hasUserConfirmedDays, setHasUserConfirmedDays] = useState(false);

  // === ÉTATS POUR LA GESTION DES CONFLITS D'AFFECTATION ===
  // Liste des conflits détectés lors de l'enregistrement du retard
  const [conflitsRetard, setConflitsRetard] = useState<{
    membre: SelectedMemberForDelay;
    affectationConflictuelle: any;
    chantierNom: string;
    dateDebut: string;
    dateFin: string;
  }[]>([]);
  // Modal de résolution des conflits
  const [showConflitRetardModal, setShowConflitRetardModal] = useState(false);
  // Membres disponibles (sans conflit)
  const [membresDisponibles, setMembresDisponibles] = useState<SelectedMemberForDelay[]>([]);
  // Membres en conflit avec leur info de conflit
  const [membresEnConflit, setMembresEnConflit] = useState<{ membre: SelectedMemberForDelay; conflit: any }[]>([]);
  // Loading state pour les actions de résolution de conflit
  const [resolvingConflict, setResolvingConflict] = useState(false);

  // === ÉTAT POUR LE CRÉNEAU LIBRE DU DERNIER JOUR ===
  // null = aucun créneau libre (journée complète occupée ou chargement)
  // 'AM' = matin libre, 'PM' = après-midi libre, 'FULL' = journée entière libre
  const [creneauLibreDernierJour, setCreneauLibreDernierJour] = useState<'AM' | 'PM' | 'FULL' | null>(null);

  // === FONCTION POUR VÉRIFIER LES AFFECTATIONS DU DERNIER JOUR ===
  // Retourne le créneau libre du dernier jour contractuel (contractEndDate)
  const getCreneauLibreDernierJour = async (dateContractEnd: Date): Promise<'AM' | 'PM' | 'FULL' | null> => {
    if (!chantierId) return null;

    const dateStr = format(dateContractEnd, 'yyyy-MM-dd');

    // Récupérer toutes les affectations du chantier qui couvrent ce jour
    const { data: affectations, error } = await supabase
      .from('affectations_chantiers')
      .select('id, jours_details, date_debut, date_fin')
      .eq('chantier_id', chantierId)
      .lte('date_debut', dateStr)
      .gte('date_fin', dateStr);

    if (error) {
      console.error('[getCreneauLibreDernierJour] Erreur Supabase:', error);
      return null;
    }

    // Si aucune affectation couvre ce jour, la journée entière est libre
    if (!affectations || affectations.length === 0) {
      return 'FULL';
    }

    // Analyser les créneaux occupés pour ce jour
    let hasAM = false;
    let hasPM = false;
    let hasFULL = false;

    for (const aff of affectations) {
      // Si jours_details est null, considérer comme FULL (journée complète occupée)
      if (!aff.jours_details) {
        hasFULL = true;
        continue;
      }

      // Parcourir les jours_details pour ce jour
      const joursDetails = aff.jours_details as Array<{ date: string; period: string; value: number }>;
      for (const jd of joursDetails) {
        if (jd.date === dateStr) {
          if (jd.period === 'AM') hasAM = true;
          if (jd.period === 'PM') hasPM = true;
          if (jd.period === 'FULL') hasFULL = true;
        }
      }
    }

    // Déterminer le créneau libre
    if (hasFULL || (hasAM && hasPM)) {
      return null; // Pas de créneau libre
    }
    if (hasAM && !hasPM) {
      return 'PM'; // Matin occupé → après-midi libre
    }
    if (!hasAM && hasPM) {
      return 'AM'; // Après-midi occupé → matin libre
    }
    return 'FULL'; // Aucune affectation → journée complète libre
  };

  // Charger le retard existant pour ce chantier
  useEffect(() => {
    async function loadExistingDelay() {
      if (!chantierId) return;

      // Si on vient d'enregistrer, ne pas recharger (les états sont déjà corrects)
      if (justSaved) {
        setJustSaved(false);
        return;
      }

      const { data, error } = await supabase
        .from('frais_chantier')
        .select('id, montant_total, description, date_frais, type_frais, membres_mobilises_ids')
        .eq('chantier_id', chantierId)
        .or('type_frais.ilike.%Pénalité de retard%,type_frais.ilike.%Coût retard%')
        .order('date_frais', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!error && data) {
        setExistingDelay({
          id: data.id,
          montant: data.montant_total,
          description: data.description || '',
          date_frais: data.date_frais,
        });
        // Extraire le nombre de jours depuis la description (supporte les décimaux comme 1.5 ou 1,5)
        const match = data.description?.match(/(\d+(?:[.,]\d+)?)\s*jour/);
        let extractedDays = 0.5;
        if (match) {
          // Remplacer la virgule par un point pour parseFloat
          extractedDays = parseFloat(match[1].replace(',', '.'));
          setDelayDays(extractedDays);
        }

        // Restaurer l'état penaltyEnabled selon le type_frais enregistré
        // "Pénalité de retard ⏳" = pénalités activées
        // "Coût retard (main d'œuvre) ⏳" = pénalités désactivées
        const hasPenaltyEnabled = data.type_frais?.includes('Pénalité de retard');
        setPenaltyEnabled(hasPenaltyEnabled);

        // Restaurer le type et la valeur de pénalité depuis la description
        // Format attendu: "... (X%/jour)" ou "... (X€/jour)" ou "... (paliers)"
        const descr = data.description || '';
        const penaltyMatch = descr.match(/\((\d+(?:[.,]\d+)?)(%)\/jour\)/) ||  // pourcentage: (0.5%/jour)
                             descr.match(/\((\d+(?:[.,]\d+)?)(€)\/jour\)/);     // fixe: (50€/jour)

        if (penaltyMatch) {
          const value = parseFloat(penaltyMatch[1].replace(',', '.'));
          const isPercentage = penaltyMatch[2] === '%';
          setPenaltyType(isPercentage ? 'percentage' : 'fixed');
          setPenaltyValue(value);
        } else if (descr.includes('(paliers)')) {
          setPenaltyType('tiered');
          // Pour les paliers, on garde les valeurs par défaut ou celles du localStorage
        }

        // Charger le coût réel depuis les affectations de retard
        // et récupérer la date de fin réelle (date_fin des affectations)
        const { data: affectationsData } = await supabase
          .from('affectations_chantiers')
          .select('jours_travailles, taux_horaire_specifique, heures_par_jour, date_fin, membre_equipe_id, membres_equipe(coefficient_charges)')
          .eq('chantier_id', chantierId)
          .like('notes', 'Retard chantier%');

        if (affectationsData && affectationsData.length > 0) {
          // Calculer le coût total et le coût journalier des affectations de retard
          let totalCost = 0;
          let totalJoursRetard = 0;
          let coutJournalierTotal = 0; // Somme des coûts journaliers de chaque membre
          let maxDateFin: string | null = null;

          for (const aff of affectationsData) {
            const jours = aff.jours_travailles || 0;
            const taux = aff.taux_horaire_specifique || 0;
            const coef = (aff as any).membres_equipe?.coefficient_charges || 1.85;
            const heures = aff.heures_par_jour || 8;
            const coutJournalierMembre = taux * coef * heures;
            const coutAffectation = jours * coutJournalierMembre;
            totalCost += coutAffectation;
            totalJoursRetard = Math.max(totalJoursRetard, jours); // Prendre le max car membres en parallèle
            coutJournalierTotal += coutJournalierMembre; // Coût journalier = somme des coûts de chaque membre

            // Garder la date de fin la plus tardive
            if (aff.date_fin && (!maxDateFin || aff.date_fin > maxDateFin)) {
              maxDateFin = aff.date_fin;
            }
          }

          setDelayCostFromAffectations(totalCost);
          setCoutJournalierEquipeRetard(coutJournalierTotal);

          // Restaurer la date de fin réelle depuis les affectations (corrige le problème de date 6→5)
          if (maxDateFin) {
            setSelectedEndDate(parse(maxDateFin, 'yyyy-MM-dd', new Date()));
          }
        } else {
          // Pas d'affectations de retard = équipe de base utilisée
          // Utiliser le coût journalier de l'équipe de base du chantier
          setCoutJournalierEquipeRetard(metrics?.cout_journalier_equipe || 0);

          // Fallback : calculer la date si pas d'affectations
          if (metrics?.date_debut && metrics?.duree_estimee_jours && extractedDays > 0) {
            const contractEnd = addBusinessDays(new Date(metrics.date_debut), metrics.duree_estimee_jours);
            const restoredEndDate = addBusinessDays(contractEnd, Math.ceil(extractedDays));
            setSelectedEndDate(restoredEndDate);
          }
        }

        // Charger les membres mobilisés depuis membres_mobilises_ids
        // IMPORTANT: Filtrer pour ne garder que les membres qui ont encore une affectation active
        // (exclure les membres dont l'affectation a été terminée via rotation)
        const membreIds = (data as any).membres_mobilises_ids as string[] | null;
        if (membreIds && membreIds.length > 0) {
          // D'abord vérifier quels membres ont encore une affectation active sur ce chantier
          const today = new Date().toISOString().split('T')[0];
          const { data: affectationsActives } = await supabase
            .from('affectations_chantiers')
            .select('membre_equipe_id')
            .eq('chantier_id', chantierId)
            .in('membre_equipe_id', membreIds)
            .gte('date_fin', today); // Affectation pas encore terminée

          // Ne garder que les IDs des membres avec affectation active
          const membresActifsIds = affectationsActives?.map(a => a.membre_equipe_id) || [];

          if (membresActifsIds.length > 0) {
            // Charger les infos des membres actifs seulement
            const { data: membresData } = await supabase
              .from('membres_equipe')
              .select('id, prenom, nom, taux_horaire, coefficient_charges, heures_par_jour, equipe_id, equipes(id, pictogramme, couleur)')
              .in('id', membresActifsIds);

            if (membresData && membresData.length > 0) {
              const membres: SelectedMemberForDelay[] = membresData.map((m: any) => ({
                id: m.id,
                prenom: m.prenom,
                nom: m.nom,
                taux_horaire: m.taux_horaire || 0,
                coefficient_charges: m.coefficient_charges || 1.85,
                heures_par_jour: m.heures_par_jour || 8,
                equipe_id: m.equipe_id,
                equipe_pictogramme: m.equipes?.pictogramme,
                equipe_couleur: m.equipes?.couleur,
              }));
              setSelectedMembersForDelay(membres);
              setTeamChoice('custom');

              // Mettre à jour membres_mobilises_ids si des membres ont été retirés
              if (membresActifsIds.length < membreIds.length) {
                await supabase
                  .from('frais_chantier')
                  .update({ membres_mobilises_ids: membresActifsIds })
                  .eq('id', data.id);

              }
            } else {
              // Plus de membres actifs, fallback équipe actuelle
              setTeamChoice('current');
            }
          } else {
            // Aucun membre n'a plus d'affectation active, fallback équipe actuelle
            setTeamChoice('current');
          }
        } else {
          // Pas de membres_mobilises_ids = équipe actuelle du chantier utilisée
          setTeamChoice('current');
        }

        // Passer en mode lecture seule (figé) car un retard existe déjà
        // SAUF si openEditMode est true (depuis clic sur "Modifier" de la ProjectCard)
        if (openEditMode) {
          setIsEditMode(true);
          toast.info('Vous pouvez maintenant modifier le retard', { duration: 3000 });
        } else {
          setIsEditMode(false);
        }
      } else {
        // Pas de retard existant, mode édition actif
        setIsEditMode(true);
      }
    }

    loadExistingDelay();
  }, [chantierId, metrics?.date_debut, metrics?.duree_estimee_jours, justSaved, openEditMode]);

  // Sauvegarder les métriques initiales au premier chargement (pour Point Critique)
  // Ceci évite que Point Critique se mette à jour après l'enregistrement du retard
  useEffect(() => {
    if (metrics && !initialMetricsRef.current) {
      initialMetricsRef.current = { ...metrics };
    }
  }, [metrics]);

  // Afficher le tips uniquement si jamais vu
  useEffect(() => {
    const fromChecklist = (location.state as { fromChecklist?: boolean })?.fromChecklist;
    const tipsSeen = localStorage.getItem(TIPS_RETARD_SEEN_KEY);

    // Mémoriser si on vient de la checklist
    if (fromChecklist) {
      setFromChecklistFlag(true);
    }

    // Afficher uniquement si jamais vu (ne plus afficher après "J'ai compris")
    if (!tipsSeen) {
      setShowTips(true);
    }
  }, [location.state]);

  // Lancer le clignotement du bouton après fermeture du tips (si on vient de la checklist)
  useEffect(() => {
    if (!showTips && fromChecklistFlag && !isBlinking && !isSliderBlinking && !isTeamButtonsBlinking) {
      // Petit délai pour laisser la modal se fermer
      const timer = setTimeout(() => {
        setIsBlinking(true);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [showTips, fromChecklistFlag]);

  // Auto-stop du clignotement bouton après 10 secondes, puis lancer slider
  useEffect(() => {
    if (isBlinking) {
      const timer = setTimeout(() => {
        setIsBlinking(false);
        setIsSliderBlinking(true);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [isBlinking]);

  // Auto-stop du clignotement slider après 10 secondes
  useEffect(() => {
    if (isSliderBlinking) {
      const timer = setTimeout(() => {
        setIsSliderBlinking(false);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [isSliderBlinking]);

  // Arrêter le clignotement quand on clique sur la section pénalité
  const handlePenaltySectionClick = () => {
    if (isBlinking) {
      setIsBlinking(false);
      // Lancer le clignotement du slider
      setIsSliderBlinking(true);
    }
  };

  // Calculer la date de fin contractuelle (depuis date_debut + duree_estimee_jours ouvrés)
  const contractEndDateLocal = React.useMemo(() => {
    if (!metrics?.date_debut || !metrics?.duree_estimee_jours) return null;
    return addBusinessDays(new Date(metrics.date_debut), metrics.duree_estimee_jours);
  }, [metrics?.date_debut, metrics?.duree_estimee_jours]);

  // Initialiser la date de fin sélectionnée par défaut (1 jour après la fin contractuelle)
  useEffect(() => {
    if (contractEndDateLocal && !selectedEndDate) {
      setSelectedEndDate(addDays(contractEndDateLocal, 1));
    }
  }, [contractEndDateLocal]);

  // === EFFET POUR CHARGER LE CRÉNEAU LIBRE DU DERNIER JOUR ===
  useEffect(() => {
    if (!chantierId || !contractEndDateLocal) {
      setCreneauLibreDernierJour(null);
      return;
    }

    const checkAffectations = async () => {
      const creneau = await getCreneauLibreDernierJour(contractEndDateLocal);

      setCreneauLibreDernierJour(creneau);
    };

    checkAffectations();
  }, [chantierId, contractEndDateLocal]);

  // Calculer le nombre de jours ouvrés entre deux dates
  const countBusinessDays = (start: Date, end: Date): number => {
    if (end <= start) return 0;
    const days = eachDayOfInterval({ start, end });
    // Exclure le premier jour (date de fin contractuelle), compter seulement les jours après
    return days.slice(1).filter(d => !isWeekend(d)).length;
  };

  // Arrêter le clignotement du slider quand on bouge le slider
  // Et synchroniser la date de fin automatiquement
  const handleSliderChange = (v: number[]) => {
    const newDelayDays = v[0];
    setDelayDays(newDelayDays);

    // Synchroniser la date de fin : ajouter X jours ouvrés depuis la date contractuelle
    if (contractEndDateLocal && newDelayDays > 0) {
      // addBusinessDays ajoute des jours ouvrés (lun-ven)
      const newEndDate = addBusinessDays(contractEndDateLocal, Math.ceil(newDelayDays));
      setSelectedEndDate(newEndDate);
      // Réinitialiser les jours sélectionnés et le flag de confirmation
      setSelectedWorkDays(new Map());
      setHasUserConfirmedDays(false);
    } else if (newDelayDays === 0) {
      setSelectedEndDate(contractEndDateLocal);
      setSelectedWorkDays(new Map());
      setHasUserConfirmedDays(false);
    }

    if (isSliderBlinking && fromChecklistFlag) {
      setIsSliderBlinking(false);
      // Lancer le clignotement des boutons équipe
      setIsTeamButtonsBlinking(true);
    } else {
      // Cas normal sans checklist
      setIsSliderBlinking(false);
    }
  };

  // Gérer la sélection manuelle d'une date de fin
  const handleDateSelect = (date: Date | undefined) => {
    if (!date || !contractEndDateLocal) return;

    setSelectedEndDate(date);

    // === CAS SPÉCIAL : Sélection du dernier jour contractuel ===
    // Si l'utilisateur sélectionne le dernier jour ET qu'un créneau est libre
    if (isSameDay(date, contractEndDateLocal) && creneauLibreDernierJour !== null) {
      const dateKey = format(date, 'yyyy-MM-dd');
      const newMap = new Map<string, number>();

      // Forcer automatiquement le créneau libre
      if (creneauLibreDernierJour === 'AM') {
        // Matin libre uniquement
        newMap.set(`${dateKey}:AM`, 0.5);
        setDelayDays(0.5);
      } else if (creneauLibreDernierJour === 'PM') {
        // Après-midi libre uniquement
        newMap.set(`${dateKey}:PM`, 0.5);
        setDelayDays(0.5);
      } else {
        // FULL : journée entière libre - pré-sélectionner le matin (utilisateur peut ajuster)
        newMap.set(`${dateKey}:AM`, 0.5);
        setDelayDays(0.5);
      }

      setSelectedWorkDays(newMap);
      setRealStartDate(date);
      setRealEndDate(date);
      setHasUserConfirmedDays(true); // Considérer comme confirmé automatiquement

      return;
    }

    // Calculer le nombre de jours ouvrés dans cette période
    const businessDaysInPeriod = countBusinessDays(contractEndDateLocal, date);

    // Si les jours ouvrés dans la période > jours du slider, ouvrir la modal de sélection
    if (businessDaysInPeriod > delayDays) {
      // Pré-sélectionner les jours ouvrés automatiquement depuis le DERNIER jour
      const allDays = eachDayOfInterval({ start: addDays(contractEndDateLocal, 1), end: date });
      const businessDays = allDays.filter(d => {
        const dayOfWeek = d.getDay();
        return dayOfWeek !== 0 && dayOfWeek !== 6;
      });

      const newMap = new Map<string, number>();
      const halfDaysToSelect = Math.round(delayDays * 2);
      let halfDaysSelected = 0;

      // Remplir depuis le DERNIER jour ouvré vers le premier (pour que la date de fin soit toujours la dernière)
      for (let i = businessDays.length - 1; i >= 0 && halfDaysSelected < halfDaysToSelect; i--) {
        const day = businessDays[i];
        const dateKey = format(day, 'yyyy-MM-dd');

        if (halfDaysSelected < halfDaysToSelect) {
          newMap.set(`${dateKey}:PM`, 0.5);
          halfDaysSelected++;
        }
        if (halfDaysSelected < halfDaysToSelect) {
          newMap.set(`${dateKey}:AM`, 0.5);
          halfDaysSelected++;
        }
      }

      setSelectedWorkDays(newMap);
      setShowDaysSelectionModal(true);
    } else {
      // Mettre à jour le slider pour correspondre aux jours ouvrés
      setDelayDays(businessDaysInPeriod);
      setSelectedWorkDays(new Map());
    }
  };

  // Calculer le total des jours sélectionnés (avec demi-journées)
  const totalSelectedDays = React.useMemo(() => {
    let total = 0;
    selectedWorkDays.forEach(value => {
      total += value;
    });
    return total;
  }, [selectedWorkDays]);

  // Toggle une demi-journée (matin ou après-midi)
  // Format clé: "YYYY-MM-DD:AM" ou "YYYY-MM-DD:PM"
  const toggleHalfDay = (day: Date, period: 'AM' | 'PM') => {
    // Utiliser format() pour éviter les problèmes de timezone
    const dateKey = format(day, 'yyyy-MM-dd');
    const halfDayKey = `${dateKey}:${period}`;

    setSelectedWorkDays(prev => {
      const newMap = new Map(prev);
      if (newMap.has(halfDayKey)) {
        newMap.delete(halfDayKey);
      } else {
        newMap.set(halfDayKey, 0.5);
      }
      return newMap;
    });
  };

  // Vérifier si une demi-journée est sélectionnée
  const isHalfDaySelected = (day: Date, period: 'AM' | 'PM'): boolean => {
    const dateKey = format(day, 'yyyy-MM-dd');
    return selectedWorkDays.has(`${dateKey}:${period}`);
  };

  // Vérifier si le jour complet est sélectionné
  const isDayFullySelected = (day: Date): boolean => {
    return isHalfDaySelected(day, 'AM') && isHalfDaySelected(day, 'PM');
  };

  // Toggle jour complet (les deux demi-journées)
  const toggleFullDay = (day: Date) => {
    const dateKey = format(day, 'yyyy-MM-dd');
    const amKey = `${dateKey}:AM`;
    const pmKey = `${dateKey}:PM`;

    setSelectedWorkDays(prev => {
      const newMap = new Map(prev);
      const hasAM = newMap.has(amKey);
      const hasPM = newMap.has(pmKey);

      if (hasAM && hasPM) {
        // Tout sélectionné → tout désélectionner
        newMap.delete(amKey);
        newMap.delete(pmKey);
      } else {
        // Pas tout sélectionné → tout sélectionner
        newMap.set(amKey, 0.5);
        newMap.set(pmKey, 0.5);
      }
      return newMap;
    });
  };

  // Extraire les dates de début et fin réelles depuis les jours sélectionnés
  const extractRealDatesFromSelection = (): { startDate: Date | null; endDate: Date | null } => {
    if (selectedWorkDays.size === 0) {
      return { startDate: null, endDate: null };
    }

    // Extraire toutes les dates uniques des clés (format "YYYY-MM-DD:AM" ou "YYYY-MM-DD:PM")
    const dates = Array.from(selectedWorkDays.keys())
      .map(key => key.split(':')[0]) // Extraire la partie date
      .filter((value, index, self) => self.indexOf(value) === index) // Unique
      .sort(); // Trier chronologiquement

    if (dates.length === 0) {
      return { startDate: null, endDate: null };
    }

    // Utiliser parse() pour créer des dates en heure locale (évite le décalage UTC)
    return {
      startDate: parse(dates[0], 'yyyy-MM-dd', new Date()),
      endDate: parse(dates[dates.length - 1], 'yyyy-MM-dd', new Date()),
    };
  };

  // Confirmer la sélection des jours travaillés
  const handleConfirmWorkDays = () => {
    // Extraire les dates réelles de début et fin
    const { startDate, endDate } = extractRealDatesFromSelection();
    setRealStartDate(startDate);
    setRealEndDate(endDate);

    // Mettre à jour le slider avec le total des jours sélectionnés
    setDelayDays(totalSelectedDays);
    setShowDaysSelectionModal(false);

    // Marquer que l'utilisateur a confirmé sa sélection (évite de réouvrir le modal)
    setHasUserConfirmedDays(true);

    // Si on attendait pour enregistrer, déclencher la sauvegarde
    if (pendingSave) {
      setPendingSave(false);
      // Afficher le modal de confirmation avant enregistrement
      setShowConfirmModal(true);
    }
  };

  // Annuler la sélection et revenir aux valeurs précédentes
  const handleCancelDaysSelection = () => {
    setShowDaysSelectionModal(false);
    // Recalculer la date basée sur le slider actuel
    if (contractEndDateLocal && delayDays > 0) {
      setSelectedEndDate(addBusinessDays(contractEndDateLocal, Math.ceil(delayDays)));
    }
    setSelectedWorkDays(new Map());
    setHasUserConfirmedDays(false);
    setPendingSave(false);
  };

  // Initialiser les jours sélectionnés par défaut quand le modal s'ouvre
  // Respecte le nombre de jours choisis par le slider (delayDays)
  // Le dernier jour (date de fin choisie) doit toujours être sélectionné
  useEffect(() => {
    if (showDaysSelectionModal && selectedWorkDays.size === 0 && contractEndDateLocal && selectedEndDate && delayDays > 0) {
      // Récupérer tous les jours de la période (y compris week-ends pour l'affichage)
      const allDays = eachDayOfInterval({ start: addDays(contractEndDateLocal, 1), end: selectedEndDate });

      // Filtrer pour ne garder que les jours ouvrés (lundi-vendredi)
      // getDay() retourne 0 pour dimanche, 6 pour samedi
      const businessDays = allDays.filter(day => {
        const dayOfWeek = day.getDay();
        return dayOfWeek !== 0 && dayOfWeek !== 6; // Exclure dimanche (0) et samedi (6)
      });


      if (businessDays.length === 0) return;

      const newMap = new Map<string, number>();

      // Nombre de demi-journées à sélectionner (1.5 jour = 3 demi-journées)
      const halfDaysToSelect = Math.round(delayDays * 2);
      let halfDaysSelected = 0;

      // Stratégie : commencer par le DERNIER jour ouvré et remonter vers le premier
      // Cela garantit que la date de fin choisie par l'utilisateur est toujours incluse
      // On remplit d'abord l'après-midi (PM) puis le matin (AM) de chaque jour
      for (let i = businessDays.length - 1; i >= 0 && halfDaysSelected < halfDaysToSelect; i--) {
        const day = businessDays[i];
        // Utiliser format() pour avoir une date locale cohérente (évite les problèmes de timezone)
        const dateKey = format(day, 'yyyy-MM-dd');

        // D'abord PM (après-midi), puis AM (matin) si on a besoin de plus
        if (halfDaysSelected < halfDaysToSelect) {
          newMap.set(`${dateKey}:PM`, 0.5);
          halfDaysSelected++;
        }
        if (halfDaysSelected < halfDaysToSelect) {
          newMap.set(`${dateKey}:AM`, 0.5);
          halfDaysSelected++;
        }
      }


      setSelectedWorkDays(newMap);
    }
  }, [showDaysSelectionModal, contractEndDateLocal, selectedEndDate, delayDays]);

  // Obtenir tous les jours de la période de retard pour la grille
  const daysInDelayPeriod = React.useMemo(() => {
    if (!contractEndDateLocal || !selectedEndDate || selectedEndDate <= contractEndDateLocal) {
      return [];
    }
    return eachDayOfInterval({ start: addDays(contractEndDateLocal, 1), end: selectedEndDate });
  }, [contractEndDateLocal, selectedEndDate]);

  // Arrêter le clignotement des boutons équipe quand on choisit
  const handleTeamChoice = (choice: 'current' | 'custom') => {
    setIsTeamButtonsBlinking(false);
    setFromChecklistFlag(false);
    setTeamChoice(choice);
    if (choice === 'custom') {
      setModalEquipeOpen(true);
    }
    // Marquer l'étape simulation-retard comme complétée dans la checklist
    localStorage.setItem(TIPS_RETARD_SEEN_KEY, 'true');
    // Déclencher un événement pour rafraîchir la checklist immédiatement
    window.dispatchEvent(new CustomEvent('b8ild-checklist-update'));
  };

  // Fermer le tips et mémoriser pour ne plus l'afficher
  const handleCloseTips = () => {
    setShowTips(false);
    localStorage.setItem(TIPS_RETARD_SEEN_KEY, 'true');
  };

  // Taux par paliers personnalisables (valeurs CCAG par défaut)
  const [tieredRates, setTieredRates] = useState<TieredPenalty[]>([
    { minDays: 1, maxDays: 5, amountPerDay: 200 },
    { minDays: 6, maxDays: 15, amountPerDay: 500 },
    { minDays: 16, maxDays: 100, amountPerDay: 1000 },
  ]);

  // Flag pour éviter de sauvegarder pendant le chargement initial
  const [isInitialized, setIsInitialized] = useState(false);

  // Charger les données persistées depuis sessionStorage au montage
  // SEULEMENT si pas de retard existant en DB (mode simulation pure)
  useEffect(() => {

    // Ne JAMAIS charger depuis sessionStorage si un retard existe déjà en DB
    // (les données viendront de la DB via le useEffect loadExistingDelay)
    if (!chantierId || existingDelay) {
      setIsInitialized(true);
      return;
    }

    const storageKey = getSessionStorageKey(chantierId);
    const savedData = sessionStorage.getItem(storageKey);

    if (savedData) {
      try {
        const parsed: PersistedDelayData = JSON.parse(savedData);

        setDelayDays(parsed.delayDays);
        setPenaltyType(parsed.penaltyType);
        setPenaltyValue(parsed.penaltyValue);
        setPenaltyEnabled(parsed.penaltyEnabled);
        setSelectedMembersForDelay(parsed.selectedMembersForDelay || []);
        setTeamChoice(parsed.teamChoice);
        if (parsed.selectedEndDate) {
          setSelectedEndDate(new Date(parsed.selectedEndDate));
        }
        if (parsed.tieredRates) {
          setTieredRates(parsed.tieredRates);
        }

      } catch (e) {
        console.error('[DelaySimulator] Erreur parsing sessionStorage:', e);
      }
    }

    // Marquer comme initialisé après un court délai pour éviter la sauvegarde immédiate
    setTimeout(() => setIsInitialized(true), 100);
  }, [chantierId, existingDelay]);

  // Sauvegarder les données dans sessionStorage quand elles changent
  useEffect(() => {
    if (!chantierId || !isInitialized || existingDelay) return; // Ne pas sauvegarder si un retard existe déjà

    const dataToSave: PersistedDelayData = {
      delayDays,
      penaltyType,
      penaltyValue,
      penaltyEnabled,
      selectedMembersForDelay,
      teamChoice,
      selectedEndDate: selectedEndDate?.toISOString() || null,
      tieredRates,
    };

    const storageKey = getSessionStorageKey(chantierId);
    sessionStorage.setItem(storageKey, JSON.stringify(dataToSave));
  }, [chantierId, isInitialized, existingDelay, delayDays, penaltyType, penaltyValue, penaltyEnabled, selectedMembersForDelay, teamChoice, selectedEndDate, tieredRates]);

  // Nettoyer sessionStorage après un enregistrement réussi
  const clearSessionStorage = () => {
    if (chantierId) {
      const key = getSessionStorageKey(chantierId);
      sessionStorage.removeItem(key);

    }
  };

  // Fonction pour mettre à jour un palier
  const updateTier = (index: number, field: keyof TieredPenalty, value: number) => {
    setTieredRates(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };
  
  const simulation = useDelayPenaltySimulation({
    metrics,
    delayDays,
    penaltyType,
    penaltyValue,
    tieredRates: penaltyType === 'tiered' ? tieredRates : undefined,
    penaltyEnabled,
    selectedMembersForDelay: selectedMembersForDelay.length > 0 ? selectedMembersForDelay : undefined,
  });

  // Simulation avec les métriques INITIALES (pour Point Critique - ne change pas après enregistrement)
  const simulationForPointCritique = useDelayPenaltySimulation({
    metrics: initialMetricsRef.current || metrics,
    delayDays,
    penaltyType,
    penaltyValue,
    tieredRates: penaltyType === 'tiered' ? tieredRates : undefined,
    penaltyEnabled,
    selectedMembersForDelay: selectedMembersForDelay.length > 0 ? selectedMembersForDelay : undefined,
  });

  // Remonter criticalDelayDays au parent quand la simulation change
  useEffect(() => {
    if (onCriticalDelayDaysChange && simulationForPointCritique) {
      onCriticalDelayDaysChange(simulationForPointCritique.criticalDelayDays);
    }
  }, [simulationForPointCritique.criticalDelayDays, onCriticalDelayDaysChange]);

  // === FONCTIONS POUR LA GESTION DES CONFLITS D'AFFECTATION ===

  /**
   * Calcule les jours ouvrés entre deux dates (excluant weekends)
   */
  const calculateJoursOuvres = (debut: Date, fin: Date): number => {
    if (fin < debut) return 0;
    const jours = eachDayOfInterval({ start: debut, end: fin });
    return jours.filter(j => !isWeekend(j)).length;
  };

  /**
   * Détecte les conflits d'affectation pour les membres sélectionnés
   * Un conflit existe si le membre est déjà affecté à un autre chantier
   * pendant la période de retard
   */
  const detecterConflitsRetard = async (
    membres: SelectedMemberForDelay[],
    dateDebutRetard: Date,
    dateFinRetard: Date
  ): Promise<{
    disponibles: SelectedMemberForDelay[];
    enConflit: { membre: SelectedMemberForDelay; conflit: any }[];
  }> => {
    const dateDebutStr = format(dateDebutRetard, 'yyyy-MM-dd');
    const dateFinStr = format(dateFinRetard, 'yyyy-MM-dd');


    // Récupérer toutes les affectations conflictuelles en une seule requête
    // On inclut jours_details pour pouvoir les mettre à jour lors d'une rotation
    const { data: affectations, error } = await supabase
      .from('affectations_chantiers')
      .select(`
        id, membre_equipe_id, date_debut, date_fin, chantier_id, heures_par_jour, jours_details,
        chantiers(nom_chantier, reference_chantier)
      `)
      .in('membre_equipe_id', membres.map(m => m.id))
      .neq('chantier_id', chantierId)
      .lte('date_debut', dateFinStr)
      .gte('date_fin', dateDebutStr);

    if (error) {
      console.error('[DelaySimulator] Erreur vérification conflits:', error);
      return { disponibles: membres, enConflit: [] };
    }


    const disponibles: SelectedMemberForDelay[] = [];
    const enConflit: { membre: SelectedMemberForDelay; conflit: any }[] = [];

    for (const membre of membres) {
      const conflitTrouve = affectations?.find(
        aff => aff.membre_equipe_id === membre.id
      );

      if (conflitTrouve) {

        enConflit.push({ membre, conflit: conflitTrouve });
      } else {
        disponibles.push(membre);
      }
    }


    return { disponibles, enConflit };
  };

  /**
   * Crée les affectations de retard pour les membres donnés
   * et émet l'événement de mise à jour des métriques
   */
  const creerAffectationsRetard = async (
    membres: SelectedMemberForDelay[],
    dateDebut: Date,
    dateFin: Date,
    notesSuffix: string = ''
  ) => {
    if (membres.length === 0) return;

    const dateDebutStr = format(dateDebut, 'yyyy-MM-dd');
    const dateFinStr = format(dateFin, 'yyyy-MM-dd');

    // Convertir les jours sélectionnés en format JSONB
    const joursDetails = selectedWorkDays.size > 0 ? convertWorkDaysToJsonb(selectedWorkDays) : null;

    const affectations = membres.map((membre) => ({
      chantier_id: chantierId,
      membre_equipe_id: membre.id,
      equipe_id: membre.equipe_id || null,
      date_debut: dateDebutStr,
      date_fin: dateFinStr,
      jours_travailles: delayDays,
      heures_par_jour: membre.heures_par_jour || 8,
      taux_horaire_specifique: membre.taux_horaire,
      charges_patronales_pct: membre.coefficient_charges || 1.85,
      notes: `Retard chantier - ${delayDays} jour(s)${notesSuffix ? ` (${notesSuffix})` : ''}`,
      jours_details: joursDetails,
    }));


    const { error } = await supabase
      .from('affectations_chantiers')
      .insert(affectations);

    if (error) {
      throw new Error(`Erreur création affectations: ${error.message}`);
    }

    // Mettre à jour le statut des membres
    const membreIds = membres.map(m => m.id);
    await supabase
      .from('membres_equipe')
      .update({ statut: 'sur_chantier' })
      .in('id', membreIds);

    // Émettre événement pour les composants non migrés
    window.dispatchEvent(new CustomEvent('chantier-metrics-updated', {
      detail: { chantierId }
    }));
    // Invalider le cache React Query
    if (entrepriseId) invalidateChantier(queryClient, entrepriseId, chantierId);

    return membres.length;
  };

  /**
   * Rafraîchit les métriques d'un chantier et émet l'événement de mise à jour
   */
  const refreshChantierMetrics = async (chantierIdToRefresh: string) => {
    try {
      const { data: calcData, error: calcError } = await supabase
        .rpc('compute_chantier_metrics', { p_chantier: chantierIdToRefresh });

      if (calcError) {
        console.error('[DelaySimulator] Erreur calcul métriques:', calcError);
        return;
      }

      if (calcData) {
        await supabase
          .from('chantier_metrics_realtime')
          .upsert({
            chantier_id: chantierIdToRefresh,
            metrics: calcData,
            updated_at: new Date().toISOString()
          });


        window.dispatchEvent(new CustomEvent('chantier-metrics-updated', {
          detail: { chantierId: chantierIdToRefresh }
        }));
        // Invalider le cache React Query
        if (entrepriseId) invalidateChantier(queryClient, entrepriseId, chantierIdToRefresh);
      }
    } catch (error) {
      console.error('[DelaySimulator] Erreur refresh métriques:', error);
    }
  };

  // === HANDLERS POUR LA RÉSOLUTION DES CONFLITS ===

  /**
   * Option A: Équipe partielle - n'affecter que les membres disponibles
   */
  const handleRetardEquipePartielle = async () => {
    setResolvingConflict(true);
    try {
      if (membresDisponibles.length === 0) {
        toast.error('Aucun membre disponible pour cette option');
        return;
      }

      // Calculer les dates de retard
      let dateDebut: Date;
      let dateFin: Date;

      if (realStartDate && realEndDate) {
        dateDebut = realStartDate;
        dateFin = realEndDate;
      } else if (simulation.contractEndDate) {
        dateDebut = addDays(simulation.contractEndDate, 1);
        dateFin = simulation.newEndDate || addBusinessDays(simulation.contractEndDate, Math.ceil(delayDays));
      } else {
        toast.error('Dates de retard non disponibles');
        return;
      }

      await creerAffectationsRetard(membresDisponibles, dateDebut, dateFin);

      // Mettre à jour selectedMembersForDelay avec uniquement les disponibles
      setSelectedMembersForDelay(membresDisponibles);

      toast.success(`${membresDisponibles.length} membre(s) affecté(s) au retard (équipe partielle)`);
      setShowConflitRetardModal(false);

      // Continuer avec le reste de l'enregistrement
      await finaliserEnregistrementRetard(membresDisponibles);
    } catch (error: any) {
      console.error('[DelaySimulator] Erreur équipe partielle:', error);
      toast.error(`Erreur: ${error.message}`);
    } finally {
      setResolvingConflict(false);
    }
  };

  /**
   * Option B: Rotation - retirer des autres chantiers puis affecter au retard
   */
  const handleRetardRotation = async () => {
    setResolvingConflict(true);
    try {
      // Calculer les dates de retard
      let dateDebutRetard: Date;
      let dateFinRetard: Date;

      if (realStartDate && realEndDate) {
        dateDebutRetard = realStartDate;
        dateFinRetard = realEndDate;
      } else if (simulation.contractEndDate) {
        dateDebutRetard = addDays(simulation.contractEndDate, 1);
        dateFinRetard = simulation.newEndDate || addBusinessDays(simulation.contractEndDate, Math.ceil(delayDays));
      } else {
        toast.error('Dates de retard non disponibles');
        return;
      }


      // Pour chaque membre en conflit, terminer son affectation actuelle
      for (const { conflit, membre } of membresEnConflit) {
        const nouvelleDateFin = addDays(dateDebutRetard, -1);
        const nouvelleDateFinStr = format(nouvelleDateFin, 'yyyy-MM-dd');

        // Recalculer jours_travailles
        const joursOuvres = calculateJoursOuvres(new Date(conflit.date_debut), nouvelleDateFin);


        // Si la nouvelle date de fin est AVANT la date de début de l'affectation,
        // l'affectation doit être SUPPRIMÉE (pas de jours restants)
        if (joursOuvres <= 0 || nouvelleDateFin < new Date(conflit.date_debut)) {

          const { error: deleteError } = await supabase
            .from('affectations_chantiers')
            .delete()
            .eq('id', conflit.id);

          if (deleteError) {
            console.error('[DelaySimulator] ❌ Erreur suppression affectation:', deleteError);
            throw deleteError;
          }

        } else {
          // Si l'affectation a des jours_details, les filtrer pour ne garder que ceux avant la nouvelle date de fin
          let joursDetailsFiltered = null;
          if (conflit.jours_details && Array.isArray(conflit.jours_details)) {
            joursDetailsFiltered = conflit.jours_details.filter(
              (detail: { date: string; period: string; value: number }) => detail.date <= nouvelleDateFinStr
            );

          }

          const updateData: any = {
            date_fin: nouvelleDateFinStr,
            jours_travailles: Math.max(1, joursOuvres) // Minimum 1 jour pour respecter la contrainte
          };

          // Mettre à jour jours_details si présents
          if (joursDetailsFiltered !== null) {
            updateData.jours_details = joursDetailsFiltered;
          }

          const { error: updateError } = await supabase
            .from('affectations_chantiers')
            .update(updateData)
            .eq('id', conflit.id);

          if (updateError) {
            console.error('[DelaySimulator] ❌ Erreur update affectation:', updateError);
            throw updateError;
          }


        }

        // Émettre événement pour mettre à jour l'autre chantier
        await refreshChantierMetrics(conflit.chantier_id);
      }

      // Maintenant affecter TOUS les membres au retard
      const tousMembres = [...membresDisponibles, ...membresEnConflit.map(c => c.membre)];
      await creerAffectationsRetard(tousMembres, dateDebutRetard, dateFinRetard);

      toast.success(`${tousMembres.length} membre(s) affecté(s) au retard (rotation effectuée)`);
      setShowConflitRetardModal(false);

      // Continuer avec le reste de l'enregistrement
      await finaliserEnregistrementRetard(tousMembres);
    } catch (error: any) {
      console.error('[DelaySimulator] Erreur rotation:', error);
      toast.error(`Erreur: ${error.message}`);
    } finally {
      setResolvingConflict(false);
    }
  };

  /**
   * Option C: Reporter - décaler les dates de retard après libération des membres
   */
  const handleRetardReporter = async () => {
    setShowConflitRetardModal(false);

    // Trouver la date de libération la plus tardive
    const dateLiberation = conflitsRetard.reduce((max, conflit) => {
      const dateFin = new Date(conflit.dateFin);
      return dateFin > max ? dateFin : max;
    }, new Date(0));

    // Décaler les dates de retard
    const nouvelleDateDebut = addDays(dateLiberation, 1);

    // Mettre à jour selectedEndDate pour décaler la période
    setSelectedEndDate(nouvelleDateDebut);

    toast.info(`Retard reporté. Nouvelles dates à partir du ${format(nouvelleDateDebut, 'dd/MM/yyyy', { locale: fr })}. Veuillez re-sélectionner les jours travaillés et enregistrer.`);
  };

  /**
   * Option D: Progressif - disponibles maintenant, autres rejoignent après
   */
  const handleRetardProgressif = async () => {
    setResolvingConflict(true);
    try {
      // Calculer les dates de retard
      let dateDebutRetard: Date;
      let dateFinRetard: Date;

      if (realStartDate && realEndDate) {
        dateDebutRetard = realStartDate;
        dateFinRetard = realEndDate;
      } else if (simulation.contractEndDate) {
        dateDebutRetard = addDays(simulation.contractEndDate, 1);
        dateFinRetard = simulation.newEndDate || addBusinessDays(simulation.contractEndDate, Math.ceil(delayDays));
      } else {
        toast.error('Dates de retard non disponibles');
        return;
      }

      let totalAffectes = 0;

      // 1. Créer affectations pour les membres disponibles (toute la période)
      if (membresDisponibles.length > 0) {
        await creerAffectationsRetard(membresDisponibles, dateDebutRetard, dateFinRetard);
        totalAffectes += membresDisponibles.length;
      }

      // 2. Pour chaque membre en conflit, créer une affectation différée
      for (const { membre, conflit } of membresEnConflit) {
        const dateLiberation = addDays(new Date(conflit.dateFin), 1);

        // Créer affectation différée si elle rentre dans la période de retard
        if (dateLiberation <= dateFinRetard) {
          // Calculer les jours pour cette période partielle
          const joursPartiels = calculateJoursOuvres(dateLiberation, dateFinRetard);

          if (joursPartiels > 0) {
            const affectationDifferee = {
              chantier_id: chantierId,
              membre_equipe_id: membre.id,
              equipe_id: membre.equipe_id || null,
              date_debut: format(dateLiberation, 'yyyy-MM-dd'),
              date_fin: format(dateFinRetard, 'yyyy-MM-dd'),
              jours_travailles: joursPartiels,
              heures_par_jour: membre.heures_par_jour || 8,
              taux_horaire_specifique: membre.taux_horaire,
              charges_patronales_pct: membre.coefficient_charges || 1.85,
              notes: `Retard chantier - ${joursPartiels} jour(s) (différée - rejoint le ${format(dateLiberation, 'dd/MM', { locale: fr })})`,
              jours_details: null,
            };

            await supabase
              .from('affectations_chantiers')
              .insert(affectationDifferee);

            totalAffectes++;
          }
        }
      }

      // Émettre événement pour les composants non migrés
      window.dispatchEvent(new CustomEvent('chantier-metrics-updated', {
        detail: { chantierId }
      }));
      // Invalider le cache React Query
      if (entrepriseId) invalidateChantier(queryClient, entrepriseId, chantierId);

      toast.success(`${totalAffectes} affectation(s) créée(s) (mode progressif)`);
      setShowConflitRetardModal(false);

      // Continuer avec le reste de l'enregistrement
      const tousMembres = [...membresDisponibles, ...membresEnConflit.map(c => c.membre)];
      await finaliserEnregistrementRetard(tousMembres);
    } catch (error: any) {
      console.error('[DelaySimulator] Erreur progressif:', error);
      toast.error(`Erreur: ${error.message}`);
    } finally {
      setResolvingConflict(false);
    }
  };

  /**
   * Finalise l'enregistrement du retard après résolution des conflits
   * (création du frais_chantier et mise à jour de l'état)
   */
  const finaliserEnregistrementRetard = async (membresFinaux: SelectedMemberForDelay[]) => {
    if (!user) return;

    const { data: entreprise } = await supabase
      .from('entreprises')
      .select('id')
      .eq('proprietaire_user_id', user.id)
      .single();

    if (!entreprise) {
      toast.error('Entreprise non trouvée');
      return;
    }

    const dateFraisToday = new Date().toISOString().split('T')[0];
    const membresMobilisesIds = membresFinaux.length > 0
      ? membresFinaux.map(m => m.id)
      : null;
    const nbMembres = membresFinaux.length;
    const membresText = nbMembres > 0 ? ` · ${nbMembres} membre${nbMembres > 1 ? 's' : ''} mobilisé${nbMembres > 1 ? 's' : ''}` : '';

    if (penaltyEnabled && simulation.penaltyAmount > 0) {
      const montantPenalites = simulation.penaltyAmount;
      const description = `Pénalité contractuelle pour ${delayDays} jour${delayDays > 1 ? 's' : ''} de retard (${penaltyType === 'percentage' ? `${penaltyValue}%/jour` : penaltyType === 'fixed' ? `${penaltyValue}€/jour` : 'paliers'})${membresText}`;

      const { data: insertedData, error } = await supabase
        .from('frais_chantier')
        .insert({
          chantier_id: chantierId,
          type_frais: 'Pénalité de retard ⏳',
          montant_total: montantPenalites,
          date_frais: dateFraisToday,
          description: description,
          entreprise_id: entreprise.id,
          membres_mobilises_ids: membresMobilisesIds,
        })
        .select('id')
        .single();

      if (error) throw error;

      if (insertedData) {
        setExistingDelay({
          id: insertedData.id,
          montant: montantPenalites,
          description: description,
          date_frais: dateFraisToday,
        });
      }
    } else {
      const description = `Retard de ${delayDays} jour${delayDays > 1 ? 's' : ''}${membresText} (coût équipe via affectations)`;

      const { data: insertedData, error } = await supabase
        .from('frais_chantier')
        .insert({
          chantier_id: chantierId,
          type_frais: 'Coût retard (main d\'œuvre) ⏳',
          montant_total: 0,
          date_frais: dateFraisToday,
          description: description,
          entreprise_id: entreprise.id,
          membres_mobilises_ids: membresMobilisesIds,
        })
        .select('id')
        .single();

      if (error) throw error;

      if (insertedData) {
        setExistingDelay({
          id: insertedData.id,
          montant: 0,
          description: description,
          date_frais: dateFraisToday,
        });
      }
    }

    // Rafraîchir les métriques
    await refreshChantierMetrics(chantierId);

    // Invalider le cache de la date de fin effective pour ce chantier
    if (entrepriseId) invalidateChantierDateFin(queryClient, entrepriseId, chantierId);

    // Nettoyer et finaliser
    toast.success(penaltyEnabled ? 'Pénalité enregistrée avec succès' : 'Retard enregistré avec succès');
    setShowConfirmModal(false);
    clearSessionStorage();
    setDelayCostFromAffectations(simulation.additionalLaborCost);

    if (membresFinaux.length > 0) {
      const coutJournalier = membresFinaux.reduce((sum, m) => {
        const heures = m.heures_par_jour || 8;
        const coef = m.coefficient_charges || 1.85;
        return sum + (m.taux_horaire * coef * heures);
      }, 0);
      setCoutJournalierEquipeRetard(coutJournalier);
    }

    setJustSaved(true);
    setIsEditMode(false);
    if (onSavePenalty) onSavePenalty(delayDays);
  };

  const handleSavePenalty = async () => {
    if (!user || !metrics) {
      toast.error('Erreur : utilisateur ou métriques non disponibles');
      return;
    }

    if (delayDays === 0) {
      toast.error('Aucun retard à enregistrer');
      return;
    }

    try {
      setSaving(true);

      const { data: entreprise } = await supabase
        .from('entreprises')
        .select('id')
        .eq('proprietaire_user_id', user.id)
        .single();

      if (!entreprise) {
        toast.error('Entreprise non trouvée');
        return;
      }

      // Récupérer les membres à utiliser pour les affectations de retard
      // Si "Équipe actuelle" est sélectionnée, récupérer les membres depuis les affectations existantes
      let membresForAffectations: SelectedMemberForDelay[] = [...selectedMembersForDelay];

      if (teamChoice === 'current' && selectedMembersForDelay.length === 0) {
        // Récupérer les membres actuellement affectés au chantier (hors retard)
        const { data: affectationsActuelles } = await supabase
          .from('affectations_chantiers')
          .select(`
            membre_equipe_id,
            heures_par_jour,
            taux_horaire_specifique,
            membres_equipe (
              id, prenom, nom, taux_horaire, coefficient_charges, heures_par_jour, equipe_id,
              equipes (id, pictogramme, couleur)
            )
          `)
          .eq('chantier_id', chantierId)
          .not('notes', 'like', 'Retard chantier%');

        if (affectationsActuelles && affectationsActuelles.length > 0) {
          // Dédupliquer par membre_equipe_id
          const membresUniques = new Map<string, SelectedMemberForDelay>();
          for (const aff of affectationsActuelles) {
            const m = aff.membres_equipe as any;
            if (m && !membresUniques.has(m.id)) {
              membresUniques.set(m.id, {
                id: m.id,
                prenom: m.prenom,
                nom: m.nom,
                taux_horaire: aff.taux_horaire_specifique ?? m.taux_horaire ?? 0,
                coefficient_charges: m.coefficient_charges || 1.85,
                heures_par_jour: aff.heures_par_jour || m.heures_par_jour || 8,
                equipe_id: m.equipe_id,
                equipe_pictogramme: m.equipes?.pictogramme,
                equipe_couleur: m.equipes?.couleur,
              });
            }
          }
          membresForAffectations = Array.from(membresUniques.values());

        }
      }

      // ========== VÉRIFICATION DES CONFLITS D'AFFECTATION ==========
      // Avant de créer les affectations, vérifier si les membres ne sont pas déjà affectés ailleurs
      if (membresForAffectations.length > 0 && simulation.contractEndDate) {
        // Calculer les dates de la période de retard
        let dateDebutRetardForCheck: Date;
        let dateFinRetardForCheck: Date;

        if (realStartDate && realEndDate) {
          dateDebutRetardForCheck = realStartDate;
          dateFinRetardForCheck = realEndDate;
        } else {
          dateDebutRetardForCheck = addDays(simulation.contractEndDate, 1);
          dateFinRetardForCheck = simulation.newEndDate
            ? simulation.newEndDate
            : addBusinessDays(simulation.contractEndDate, Math.ceil(delayDays));
        }

        // Détecter les conflits
        const { disponibles, enConflit } = await detecterConflitsRetard(
          membresForAffectations,
          dateDebutRetardForCheck,
          dateFinRetardForCheck
        );

        if (enConflit.length > 0) {
          // Stocker les infos de conflit
          setMembresDisponibles(disponibles);
          setMembresEnConflit(enConflit);
          setConflitsRetard(enConflit.map(c => ({
            membre: c.membre,
            affectationConflictuelle: c.conflit,
            chantierNom: c.conflit.chantiers?.nom_chantier || c.conflit.chantiers?.reference_chantier || 'Chantier inconnu',
            dateDebut: c.conflit.date_debut,
            dateFin: c.conflit.date_fin,
          })));
          setShowConflitRetardModal(true);
          setSaving(false);
          return; // Stopper ici, attendre choix utilisateur
        }
      }
      // ========== FIN VÉRIFICATION CONFLITS ==========

      // ========== SAUVEGARDER DATE_FIN_INITIALE SI PREMIER RETARD ==========
      // Vérifier si c'est le premier retard et sauvegarder la date de fin initiale
      const { data: chantierData } = await supabase
        .from('chantiers')
        .select('date_fin_initiale, date_fin_estimee')
        .eq('id', chantierId)
        .single();

      // Si date_fin_initiale n'est pas encore définie, c'est le premier retard
      // On sauvegarde la date_fin_estimee actuelle comme date de fin initiale
      if (chantierData && !chantierData.date_fin_initiale && chantierData.date_fin_estimee) {
        const { error: updateError } = await supabase
          .from('chantiers')
          .update({ date_fin_initiale: chantierData.date_fin_estimee })
          .eq('id', chantierId);

        if (updateError) {
          console.error('[DelaySimulator] Erreur sauvegarde date_fin_initiale:', updateError);
        } else {

        }
      }
      // ========== FIN SAUVEGARDE DATE_FIN_INITIALE ==========

      // SOLUTION 3 : Le coût équipe est calculé via affectations_chantiers
      // On ne crée un frais_chantier QUE pour les pénalités contractuelles (pas le coût main d'œuvre)
      // Cela évite le double comptage (frais + affectations)

      const dateFraisToday = new Date().toISOString().split('T')[0];
      const membresMobilisesIds = membresForAffectations.length > 0
        ? membresForAffectations.map(m => m.id)
        : null;
      const nbMembres = membresForAffectations.length;
      const membresText = nbMembres > 0 ? ` · ${nbMembres} membre${nbMembres > 1 ? 's' : ''} mobilisé${nbMembres > 1 ? 's' : ''}` : '';

      if (penaltyEnabled && simulation.penaltyAmount > 0) {
        // Créer un frais UNIQUEMENT pour les pénalités contractuelles (pas le coût équipe)
        const montantPenalites = simulation.penaltyAmount;
        const description = `Pénalité contractuelle pour ${delayDays} jour${delayDays > 1 ? 's' : ''} de retard (${penaltyType === 'percentage' ? `${penaltyValue}%/jour` : penaltyType === 'fixed' ? `${penaltyValue}€/jour` : 'paliers'})${membresText}`;

        const { data: insertedData, error } = await supabase
          .from('frais_chantier')
          .insert({
            chantier_id: chantierId,
            type_frais: 'Pénalité de retard ⏳',
            montant_total: montantPenalites,
            date_frais: dateFraisToday,
            description: description,
            entreprise_id: entreprise.id,
            membres_mobilises_ids: membresMobilisesIds,
          })
          .select('id')
          .single();

        if (error) throw error;

        if (insertedData) {
          setExistingDelay({
            id: insertedData.id,
            montant: montantPenalites,
            description: description,
            date_frais: dateFraisToday,
          });
        }
      } else {
        // Pas de pénalités : créer un frais "marqueur" à 0€ pour tracker le retard
        // et pouvoir restaurer les membres mobilisés (le coût réel vient des affectations)
        const description = `Retard de ${delayDays} jour${delayDays > 1 ? 's' : ''}${membresText} (coût équipe via affectations)`;

        const { data: insertedData, error } = await supabase
          .from('frais_chantier')
          .insert({
            chantier_id: chantierId,
            type_frais: 'Coût retard (main d\'œuvre) ⏳',
            montant_total: 0, // Le coût réel vient des affectations_chantiers
            date_frais: dateFraisToday,
            description: description,
            entreprise_id: entreprise.id,
            membres_mobilises_ids: membresMobilisesIds,
          })
          .select('id')
          .single();

        if (error) throw error;

        if (insertedData) {
          setExistingDelay({
            id: insertedData.id,
            montant: 0,
            description: description,
            date_frais: dateFraisToday,
          });
        }
      }

      // Créer les affectations pour les membres (équipe actuelle ou personnalisée)
      // et mettre à jour leur statut à 'sur_chantier'
      if (membresForAffectations.length > 0 && simulation.contractEndDate) {
        // Utiliser les dates réelles sélectionnées par l'utilisateur dans le modal de sélection des jours
        // Si non disponibles, calculer les dates par défaut (fallback)
        let dateDebut: string;
        let dateFin: string;

        if (realStartDate && realEndDate) {
          // Dates réelles extraites de la sélection de l'utilisateur
          dateDebut = format(realStartDate, 'yyyy-MM-dd');
          dateFin = format(realEndDate, 'yyyy-MM-dd');
        } else {
          // Fallback : calcul par défaut si les dates réelles ne sont pas disponibles
          const debutRetard = addDays(simulation.contractEndDate, 1);
          dateDebut = format(debutRetard, 'yyyy-MM-dd');
          dateFin = simulation.newEndDate
            ? format(simulation.newEndDate, 'yyyy-MM-dd')
            : format(addBusinessDays(simulation.contractEndDate, Math.ceil(delayDays)), 'yyyy-MM-dd');
        }

        // Convertir les jours sélectionnés en format JSONB pour stockage précis
        const joursDetails = selectedWorkDays.size > 0 ? convertWorkDaysToJsonb(selectedWorkDays) : null;

        const affectations = membresForAffectations.map((membre) => ({
          chantier_id: chantierId,
          membre_equipe_id: membre.id,
          equipe_id: membre.equipe_id || null,
          date_debut: dateDebut,
          date_fin: dateFin,
          jours_travailles: delayDays,
          heures_par_jour: membre.heures_par_jour || 8,
          taux_horaire_specifique: membre.taux_horaire,
          charges_patronales_pct: membre.coefficient_charges || 1.85,
          notes: `Retard chantier - ${delayDays} jour(s)`,
          jours_details: joursDetails, // Détail précis des jours/demi-journées sélectionnés
        }));


        const { data: insertedData, error: affectationError } = await supabase
          .from('affectations_chantiers')
          .insert(affectations)
          .select();

        if (affectationError) {
          console.error('[DelaySimulator] Erreur création affectations:', affectationError);
          toast.error(`Erreur création affectations: ${affectationError.message}`);
        } else {

          // Mettre à jour le statut des membres à 'sur_chantier'
          const membreIds = membresForAffectations.map(m => m.id);
          const { error: statutError } = await supabase
            .from('membres_equipe')
            .update({ statut: 'sur_chantier' })
            .in('id', membreIds);

          if (statutError) {
            console.error('[DelaySimulator] Erreur mise à jour statut membres:', statutError);
          } else {

          }

          toast.success(`${membresForAffectations.length} membre(s) affecté(s) au chantier pour la période de retard`);
        }
      }

      // NOTE: On n'ajoute PAS dans equipe_chantier car le coût du retard est déjà
      // enregistré dans frais_chantier. L'ajouter créerait un double comptage des coûts.

      toast.success(penaltyEnabled ? 'Pénalité enregistrée avec succès' : 'Retard enregistré avec succès');
      setShowConfirmModal(false);
      // Nettoyer le sessionStorage car les données sont maintenant en DB
      clearSessionStorage();
      // Mettre à jour le coût des affectations pour l'affichage dans le banner
      setDelayCostFromAffectations(simulation.additionalLaborCost);
      // Calculer et mettre à jour le coût journalier de l'équipe mobilisée
      // (nécessaire pour le calcul des jours restants post-retard dans le Bilan)
      if (membresForAffectations.length > 0) {
        const coutJournalier = membresForAffectations.reduce((sum, m) => {
          const heures = m.heures_par_jour || 8;
          const coef = m.coefficient_charges || 1.85;
          return sum + (m.taux_horaire * coef * heures);
        }, 0);
        setCoutJournalierEquipeRetard(coutJournalier);

      }
      // Marquer qu'on vient d'enregistrer pour éviter le rechargement qui écraserait les états
      setJustSaved(true);
      // Passer en mode lecture seule après enregistrement
      setIsEditMode(false);
      // Ne pas changer teamChoice si on a utilisé l'équipe actuelle
      // (teamChoice reste 'current' ou 'custom' selon le choix initial)
      if (onSavePenalty) onSavePenalty(delayDays);
    } catch (error: any) {
      console.error('Erreur enregistrement:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  // Modifier le retard existant (mise à jour du frais)
  const handleUpdateDelay = async () => {
    if (!user || !metrics || !existingDelay) {
      toast.error('Erreur : données manquantes');
      return;
    }

    if (delayDays === 0) {
      toast.error('Veuillez spécifier un nombre de jours de retard');
      return;
    }

    try {
      setSaving(true);

      // Extraire l'ancien nombre de jours depuis la description existante
      const oldMatch = existingDelay.description?.match(/(\d+(?:[.,]\d+)?)\s*jour/);
      const oldDelayDays = oldMatch ? parseFloat(oldMatch[1].replace(',', '.')) : 0;
      const delayDiff = delayDays - oldDelayDays;

      // Récupérer les membres à utiliser pour les affectations de retard
      // Si "Équipe actuelle" est sélectionnée, récupérer les membres depuis les affectations existantes
      let membresForAffectations: SelectedMemberForDelay[] = [...selectedMembersForDelay];

      if (teamChoice === 'current' && selectedMembersForDelay.length === 0) {
        // Récupérer les membres actuellement affectés au chantier (hors retard)
        const { data: affectationsActuelles } = await supabase
          .from('affectations_chantiers')
          .select(`
            membre_equipe_id,
            heures_par_jour,
            taux_horaire_specifique,
            membres_equipe (
              id, prenom, nom, taux_horaire, coefficient_charges, heures_par_jour, equipe_id,
              equipes (id, pictogramme, couleur)
            )
          `)
          .eq('chantier_id', chantierId)
          .not('notes', 'like', 'Retard chantier%');

        if (affectationsActuelles && affectationsActuelles.length > 0) {
          // Dédupliquer par membre_equipe_id
          const membresUniques = new Map<string, SelectedMemberForDelay>();
          for (const aff of affectationsActuelles) {
            const m = aff.membres_equipe as any;
            if (m && !membresUniques.has(m.id)) {
              membresUniques.set(m.id, {
                id: m.id,
                prenom: m.prenom,
                nom: m.nom,
                taux_horaire: aff.taux_horaire_specifique ?? m.taux_horaire ?? 0,
                coefficient_charges: m.coefficient_charges || 1.85,
                heures_par_jour: aff.heures_par_jour || m.heures_par_jour || 8,
                equipe_id: m.equipe_id,
                equipe_pictogramme: m.equipes?.pictogramme,
                equipe_couleur: m.equipes?.couleur,
              });
            }
          }
          membresForAffectations = Array.from(membresUniques.values());

        }
      }

      // SOLUTION 3 : Même logique que handleSavePenalty
      // Le coût équipe vient des affectations, seules les pénalités vont dans frais_chantier

      const membresMobilisesIds = membresForAffectations.length > 0
        ? membresForAffectations.map(m => m.id)
        : null;
      const nbMembres = membresForAffectations.length;
      const membresText = nbMembres > 0 ? ` · ${nbMembres} membre${nbMembres > 1 ? 's' : ''} mobilisé${nbMembres > 1 ? 's' : ''}` : '';

      let montant: number;
      let typeFrais: string;
      let description: string;

      if (penaltyEnabled && simulation.penaltyAmount > 0) {
        montant = simulation.penaltyAmount; // Uniquement les pénalités contractuelles
        typeFrais = 'Pénalité de retard ⏳';
        description = `Pénalité contractuelle pour ${delayDays} jour${delayDays > 1 ? 's' : ''} de retard (${penaltyType === 'percentage' ? `${penaltyValue}%/jour` : penaltyType === 'fixed' ? `${penaltyValue}€/jour` : 'paliers'})${membresText}`;
      } else {
        montant = 0; // Le coût réel vient des affectations_chantiers
        typeFrais = 'Coût retard (main d\'œuvre) ⏳';
        description = `Retard de ${delayDays} jour${delayDays > 1 ? 's' : ''}${membresText} (coût équipe via affectations)`;
      }

      const { error } = await supabase
        .from('frais_chantier')
        .update({
          type_frais: typeFrais,
          montant_total: montant,
          description: description,
          membres_mobilises_ids: membresMobilisesIds,
        })
        .eq('id', existingDelay.id);

      if (error) throw error;

      // TOUJOURS supprimer les anciennes affectations de retard AVANT de créer les nouvelles
      // Cela évite le cumul en cas de modification

      const { error: deleteError, count: deletedCount } = await supabase
        .from('affectations_chantiers')
        .delete()
        .eq('chantier_id', chantierId)
        .like('notes', 'Retard chantier%');

      if (deleteError) {
        console.error('[DelaySimulator Update] Erreur suppression affectations:', deleteError);
        // Ne pas bloquer, mais logger l'erreur
      } else {

      }

      // Gérer les affectations : créer les nouvelles
      if (membresForAffectations.length > 0 && simulation.contractEndDate) {
        // Utiliser les dates réelles sélectionnées par l'utilisateur dans le modal de sélection des jours
        // Si non disponibles, calculer les dates par défaut (fallback)
        let dateDebut: string;
        let dateFin: string;

        if (realStartDate && realEndDate) {
          // Dates réelles extraites de la sélection de l'utilisateur
          dateDebut = format(realStartDate, 'yyyy-MM-dd');
          dateFin = format(realEndDate, 'yyyy-MM-dd');
        } else {
          // Fallback : calcul par défaut si les dates réelles ne sont pas disponibles
          const debutRetard = addDays(simulation.contractEndDate, 1);
          dateDebut = format(debutRetard, 'yyyy-MM-dd');
          dateFin = simulation.newEndDate
            ? format(simulation.newEndDate, 'yyyy-MM-dd')
            : format(addBusinessDays(simulation.contractEndDate, Math.ceil(delayDays)), 'yyyy-MM-dd');
        }

        // Convertir les jours sélectionnés en format JSONB pour stockage précis
        const joursDetails = selectedWorkDays.size > 0 ? convertWorkDaysToJsonb(selectedWorkDays) : null;

        // Créer les nouvelles affectations
        const affectations = membresForAffectations.map((membre) => ({
          chantier_id: chantierId,
          membre_equipe_id: membre.id,
          equipe_id: membre.equipe_id || null,
          date_debut: dateDebut,
          date_fin: dateFin,
          jours_travailles: delayDays,
          heures_par_jour: membre.heures_par_jour || 8,
          taux_horaire_specifique: membre.taux_horaire,
          charges_patronales_pct: membre.coefficient_charges || 1.85,
          notes: `Retard chantier - ${delayDays} jour(s)`,
          jours_details: joursDetails, // Détail précis des jours/demi-journées sélectionnés
        }));


        const { data: insertedData, error: affectationError } = await supabase
          .from('affectations_chantiers')
          .insert(affectations)
          .select();

        if (affectationError) {
          console.error('[DelaySimulator Update] Erreur création affectations:', affectationError);
          toast.error(`Erreur création affectations: ${affectationError.message}`);
        } else {

          // Mettre à jour le statut des membres à 'sur_chantier'
          const membreIds = membresForAffectations.map(m => m.id);
          const { error: statutError } = await supabase
            .from('membres_equipe')
            .update({ statut: 'sur_chantier' })
            .in('id', membreIds);

          if (statutError) {
            console.error('[DelaySimulator Update] Erreur statut:', statutError);
          } else {

          }
        }
      }

      // Mettre à jour l'état local
      setExistingDelay({
        ...existingDelay,
        montant,
        description,
      });

      toast.success('Retard modifié avec succès');
      // Nettoyer le sessionStorage car les données sont maintenant en DB
      clearSessionStorage();

      // Calculer le coût des affectations DIRECTEMENT depuis les membres
      // (au lieu d'utiliser simulation.additionalLaborCost qui peut être basé sur d'anciennes métriques)
      if (membresForAffectations.length > 0) {
        let coutTotalAffectations = 0;
        let coutJournalier = 0;

        for (const m of membresForAffectations) {
          const heures = m.heures_par_jour || 8;
          const coef = m.coefficient_charges || 1.85;
          const coutJournalierMembre = m.taux_horaire * coef * heures;
          coutJournalier += coutJournalierMembre;
          coutTotalAffectations += coutJournalierMembre * delayDays;
        }

        setDelayCostFromAffectations(coutTotalAffectations);
        setCoutJournalierEquipeRetard(coutJournalier);

      } else {
        // Fallback si pas de membres (ne devrait pas arriver)
        setDelayCostFromAffectations(simulation.additionalLaborCost);
      }
      // Marquer qu'on vient d'enregistrer pour éviter le rechargement qui écraserait les états
      setJustSaved(true);
      // Repasser en mode lecture seule après modification
      setIsEditMode(false);
      // Ne pas changer teamChoice si on a utilisé l'équipe actuelle
      // (teamChoice reste 'current' ou 'custom' selon le choix initial)
      if (onSavePenalty) onSavePenalty(delayDays);
    } catch (error: any) {
      console.error('Erreur modification:', error);
      toast.error('Erreur lors de la modification');
    } finally {
      setSaving(false);
    }
  };

  // Générer un rapport de retard en PDF stylisé (style rapport de clôture)
  const handleGenerateReport = async () => {
    if (!metrics) return;

    try {
      setGeneratingPdf(true);

      // Calcul prévisionnel (pour comparaison) - exclure le retard existant des coûts fixes
      const existingDelayAmount = existingDelay?.montant || 0;
      const coutFixesHorsRetard = metrics.couts_fixes_engages - existingDelayAmount;
      const coutMainOeuvrePrevisionnel = metrics.duree_estimee_jours * metrics.cout_journalier_equipe;
      const coutsTotauxPrevisionnels = coutFixesHorsRetard + coutMainOeuvrePrevisionnel;
      const margePrevisionnelle = metrics.budget_ht - coutsTotauxPrevisionnels;
      const rentabilitePrevisionnelle = metrics.budget_ht > 0
        ? (margePrevisionnelle / metrics.budget_ht) * 100
        : 0;

      // Calcul avec retard
      const margeAvecRetard = margePrevisionnelle - simulation.penaltyAmount - simulation.additionalLaborCost;
      const rentabiliteAvecRetard = metrics.budget_ht > 0
        ? (margeAvecRetard / metrics.budget_ht) * 100
        : 0;

      // Préparer les données pour le PDF
      const rapportData: RapportRetardData = {
        nomChantier: nomChantier || 'Chantier',
        client: client,
        budgetHt: metrics.budget_ht,
        dureePrevue: metrics.duree_estimee_jours,
        dateFinPrevue: simulation.contractEndDate ? format(simulation.contractEndDate, 'dd/MM/yyyy') : undefined,
        joursRetard: delayDays,
        penalitesActivees: penaltyEnabled,
        typePenalite: penaltyType,
        valeurPenalite: penaltyValue,
        coutMainOeuvre: simulation.additionalLaborCost,
        montantPenalites: simulation.penaltyAmount,
        coutTotalRetard: simulation.totalAdditionalCost,
        pointCritique: simulation.criticalDelayDays,
        coutsSansRetard: coutsTotauxPrevisionnels,
        coutsAvecRetard: coutsTotauxPrevisionnels + simulation.totalAdditionalCost,
        margeSansRetard: margePrevisionnelle,
        margeAvecRetard: margeAvecRetard,
        rentabiliteSansRetard: rentabilitePrevisionnelle,
        rentabiliteAvecRetard: rentabiliteAvecRetard,
        recommandations: simulation.recommendations,
      };

      // Générer le PDF avec @react-pdf/renderer
      const blob = await pdf(<RapportRetardPDF data={rapportData} />).toBlob();

      // Télécharger le PDF
      const cleanName = (nomChantier || 'chantier')
        .replace(/[^a-z0-9]/gi, '_')
        .toLowerCase();
      const fileName = `rapport_retard_${cleanName}_${new Date().toISOString().split('T')[0]}.pdf`;

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Rapport PDF téléchargé');

    } catch (error) {
      console.error('Erreur génération PDF:', error);
      toast.error('Erreur lors de la génération du rapport');
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Supprimer le retard existant
  const handleDeleteDelay = async () => {
    if (!existingDelay) return;

    try {
      setDeleting(true);

      // D'abord supprimer les affectations de retard associées
      // (celles avec "Retard chantier" dans les notes)
      // Utilise .like() au lieu de .ilike() pour éviter les problèmes d'encodage URL avec %
      const { data: affectationsRetard } = await supabase
        .from('affectations_chantiers')
        .select('membre_equipe_id')
        .eq('chantier_id', chantierId)
        .like('notes', 'Retard chantier%');

      // Supprimer les affectations
      await supabase
        .from('affectations_chantiers')
        .delete()
        .eq('chantier_id', chantierId)
        .like('notes', 'Retard chantier%');

      // Remettre le statut des membres à 'disponible' si ils n'ont plus d'autres affectations actives
      if (affectationsRetard && affectationsRetard.length > 0) {
        const membreIds = affectationsRetard.map(a => a.membre_equipe_id).filter(Boolean);
        const today = new Date().toISOString().split('T')[0];

        for (const membreId of membreIds) {
          // Vérifier si le membre a d'autres affectations actives
          const { count } = await supabase
            .from('affectations_chantiers')
            .select('*', { count: 'exact', head: true })
            .eq('membre_equipe_id', membreId)
            .gte('date_fin', today);

          if (count === 0) {
            // Pas d'autres affectations, remettre à disponible
            await supabase
              .from('membres_equipe')
              .update({ statut: 'disponible' })
              .eq('id', membreId);
          }
        }
      }

      // Supprimer le frais
      const { error } = await supabase
        .from('frais_chantier')
        .delete()
        .eq('id', existingDelay.id);

      if (error) throw error;

      setExistingDelay(null);
      setDelayDays(0.5);
      setSelectedMembersForDelay([]);
      setTeamChoice(null);
      setShowDeleteModal(false);
      // Repasser en mode édition après suppression
      setIsEditMode(true);
      toast.success('Retard supprimé avec succès');
      if (onSavePenalty) onSavePenalty(0);
    } catch (error: any) {
      console.error('Erreur suppression:', error);
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  if (!metrics) {
    return (
      <Card>
        <CardContent className="pt-16 pb-16 text-center">
          <AlertTriangle className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
          <p className="text-xl font-semibold text-muted-foreground">
            Aucune métrique disponible
          </p>
        </CardContent>
      </Card>
    );
  }
  
  // Détermine si les contrôles sont verrouillés (retard enregistré et pas en mode édition)
  const isLocked = existingDelay !== null && !isEditMode;

  return (
    <div className="space-y-6">
      {/* Bandeau MODE SIMULATION ou RETARD ENREGISTRÉ */}
      {isLocked ? (
        <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <Lock className="h-6 w-6 text-green-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-green-200">RETARD ENREGISTRÉ</p>
                <p className="text-sm text-green-200/80">
                  {/* Afficher le coût réel: pénalités + coût main d'œuvre des affectations */}
                  {(existingDelay.montant + delayCostFromAffectations).toFixed(2)} € — {existingDelay.description}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditMode(true)}
              className="border-green-500/50 text-green-400 hover:bg-green-500/10"
            >
              <Pencil className="mr-2 h-4 w-4" />
              Modifier
            </Button>
          </div>
        </div>
      ) : (
        <div className="bg-amber-500/20 border border-amber-500/50 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Beaker className="h-6 w-6 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-200">MODE SIMULATION</p>
              <p className="text-sm text-amber-200/80">
                Les données ci-dessous sont hypothétiques. Cliquez sur "Enregistrer
                pénalité réelle" uniquement si le retard est confirmé.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>📋 Configuration de la Simulation</CardTitle>
          <CardDescription>
            Simulez l'impact financier d'un retard sur votre projet
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Activer/désactiver les pénalités */}
          <div
            className={cn(
              "flex items-center space-x-3 p-4 bg-muted/30 border border-border rounded-lg transition-all duration-300",
              isBlinking && "animate-pulse-soft ring-2 ring-primary/50 ring-offset-2 ring-offset-background"
            )}
            onClick={handlePenaltySectionClick}
          >
            <Checkbox
              id="penalty-enabled"
              checked={penaltyEnabled}
              onCheckedChange={(checked) => setPenaltyEnabled(checked === true)}
              className="h-5 w-5"
              disabled={isLocked}
            />
            <label htmlFor="penalty-enabled" className="cursor-pointer text-lg font-semibold text-foreground">
              Appliquer les pénalités de retard
            </label>
          </div>

          {/* Section collapsible des paramètres de pénalité */}
          <div
            className={cn(
              "grid md:grid-cols-2 gap-6 overflow-hidden transition-all duration-300 ease-in-out",
              penaltyEnabled ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
            )}
          >
            {/* Type de pénalité */}
            <div className="space-y-2">
              <Label>Type de pénalité</Label>
              <Select value={penaltyType} onValueChange={(v: any) => setPenaltyType(v)} disabled={isLocked}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">
                    <span className="font-semibold">% du montant HT</span>
                    <span className="text-muted-foreground ml-1">(/jour)</span>
                  </SelectItem>
                  <SelectItem value="fixed">
                    <span className="font-semibold">€ fixe</span>
                    <span className="text-muted-foreground ml-1">(/jour de retard)</span>
                  </SelectItem>
                  <SelectItem value="tiered">
                    <span className="font-semibold">📊 Paliers progressifs</span>
                    <span className="text-muted-foreground ml-1">(CCAG)</span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Valeur de la pénalité */}
            {penaltyType !== 'tiered' && (
              <div className="space-y-2">
                <Label>
                  {penaltyType === 'percentage' ? 'Taux (% par jour)' : 'Montant (€ par jour)'}
                </Label>
                <Input
                  type="number"
                  step={penaltyType === 'percentage' ? '0.1' : '10'}
                  value={penaltyValue}
                  onChange={(e) => setPenaltyValue(parseFloat(e.target.value) || 0)}
                  placeholder={penaltyType === 'percentage' ? '0.5' : '500'}
                  disabled={isLocked}
                />
              </div>
            )}

            {penaltyType === 'tiered' && (
              <div className="space-y-3">
                <Label>Paliers personnalisés</Label>
                {tieredRates.map((tier, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground w-6">J</span>
                    <Input
                      type="number"
                      className="w-16 h-8 text-center"
                      value={tier.minDays}
                      onChange={(e) => updateTier(idx, 'minDays', parseInt(e.target.value) || 0)}
                      disabled={isLocked}
                    />
                    <span className="text-muted-foreground">-</span>
                    <Input
                      type="number"
                      className="w-16 h-8 text-center"
                      value={tier.maxDays}
                      onChange={(e) => updateTier(idx, 'maxDays', parseInt(e.target.value) || 0)}
                      disabled={isLocked}
                    />
                    <span className="text-muted-foreground">:</span>
                    <Input
                      type="number"
                      className="w-24 h-8 text-center"
                      value={tier.amountPerDay}
                      onChange={(e) => updateTier(idx, 'amountPerDay', parseInt(e.target.value) || 0)}
                      disabled={isLocked}
                    />
                    <span className="text-muted-foreground text-xs">€/jour</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Slider de jours ouvrés travaillés - AVANT les dates */}
          <div className="space-y-4 p-4 bg-primary/5 border border-primary/20 rounded-lg">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-foreground">
                Jours travaillés <span className="text-sm font-normal text-muted-foreground">(équipe mobilisée)</span>
              </h3>
              <span className="text-3xl font-black text-primary">{delayDays} <span className="text-lg font-medium">jour{delayDays > 1 ? 's' : ''}</span></span>
            </div>
            <Slider
              value={[delayDays]}
              onValueChange={handleSliderChange}
              min={0}
              max={130}
              step={0.5}
              className="py-4"
              thumbBlink={isSliderBlinking}
              disabled={isLocked}
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>0</span>
              <span>1 mois</span>
              <span>2 mois</span>
              <span>3 mois</span>
              <span>4 mois</span>
              <span>5 mois</span>
              <span>6 mois</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Nombre de jours où l'équipe sera mobilisée pendant la période de retard (peut être différent du nombre de jours calendaires)
            </p>
          </div>

          {/* Dates de fin - APRÈS le slider */}
          {contractEndDateLocal && (
            <div className="grid md:grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Date de fin contractuelle</p>
                <p className="font-semibold">
                  {format(contractEndDateLocal, 'dd MMMM yyyy', { locale: fr })}
                </p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground mb-1">Nouvelle date de fin réelle</Label>
                {isLocked ? (
                  // Mode figé : afficher la date sans bouton pour une meilleure lisibilité
                  <div className="flex items-center gap-2 py-2 px-3 rounded-md bg-orange-500/10 border border-orange-500/50">
                    <CalendarDays className="h-4 w-4 text-orange-500" />
                    <span className="font-semibold text-orange-500">
                      {selectedEndDate ? format(selectedEndDate, 'dd MMMM yyyy', { locale: fr }) : '-'}
                    </span>
                  </div>
                ) : (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-semibold text-orange-600 border-orange-500/50 hover:bg-orange-500/10",
                        !selectedEndDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarDays className="mr-2 h-4 w-4" />
                      {selectedEndDate ? format(selectedEndDate, 'dd MMMM yyyy', { locale: fr }) : 'Sélectionner une date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={selectedEndDate || undefined}
                      onSelect={handleDateSelect}
                      disabled={(date) => {
                        if (!contractEndDateLocal) return false;
                        // Permettre le dernier jour SI un créneau est libre
                        if (isSameDay(date, contractEndDateLocal)) {
                          return creneauLibreDernierJour === null;
                        }
                        return date < contractEndDateLocal;
                      }}
                      initialFocus
                      locale={fr}
                    />
                  </PopoverContent>
                </Popover>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sélection équipe retard - AVANT le comparatif */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Équipe mobilisée pendant le retard
          </CardTitle>
          <CardDescription>
            Quelle équipe travaillera pendant les {delayDays} jours de retard ?
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Si pas encore de choix, afficher les deux options */}
          {teamChoice === null ? (
            <div className="grid sm:grid-cols-2 gap-3">
              <Button
                variant="outline"
                className={cn(
                  "h-auto py-4 flex flex-col items-center gap-2 hover:bg-primary/5 hover:border-primary/50",
                  isTeamButtonsBlinking && "animate-pulse-soft"
                )}
                onClick={() => handleTeamChoice('current')}
                disabled={delayDays === 0 || isLocked}
              >
                <Users className="h-6 w-6 text-primary" />
                <span className="font-semibold">Équipe actuelle</span>
                <span className="text-xs text-muted-foreground text-center">
                  Conserver l'équipe déjà affectée au chantier
                </span>
              </Button>
              <Button
                variant="outline"
                className={cn(
                  "h-auto py-4 flex flex-col items-center gap-2 hover:bg-accent/10 hover:border-accent/50",
                  isTeamButtonsBlinking && "animate-pulse-soft"
                )}
                onClick={() => handleTeamChoice('custom')}
                disabled={delayDays === 0 || isLocked}
              >
                <Users className="h-6 w-6 text-accent" />
                <span className="font-semibold">Équipe personnalisée</span>
                <span className="text-xs text-muted-foreground text-center">
                  Sélectionner d'autres membres
                </span>
              </Button>
            </div>
          ) : (
            /* Affichage collapsé après le choix */
            <div className="flex items-center justify-between p-4 bg-muted/30 border border-border rounded-lg">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                <div>
                  {teamChoice === 'current' ? (
                    <p className="font-semibold text-foreground">
                      Équipe actuelle du chantier mobilisée
                    </p>
                  ) : (
                    <div>
                      <p className="font-semibold text-foreground">
                        {selectedMembersForDelay.length} membre{selectedMembersForDelay.length > 1 ? 's' : ''} sélectionné{selectedMembersForDelay.length > 1 ? 's' : ''}
                      </p>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {selectedMembersForDelay.slice(0, 5).map((m) => (
                          <Badge key={m.id} variant="secondary" className="text-xs flex items-center gap-1 pl-1">
                            {m.equipe_pictogramme && m.equipe_couleur ? (
                              <EquipeIcon
                                pictogramme={m.equipe_pictogramme}
                                couleur={m.equipe_couleur}
                                size="xs"
                                showBackground={false}
                              />
                            ) : null}
                            {m.prenom} {m.nom}
                          </Badge>
                        ))}
                        {selectedMembersForDelay.length > 5 && (
                          <Badge variant="secondary" className="text-xs">
                            +{selectedMembersForDelay.length - 5} autres
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                disabled={isLocked}
                onClick={() => {
                  setTeamChoice(null);
                  if (teamChoice === 'custom') {
                    setSelectedMembersForDelay([]);
                  }
                }}
              >
                Modifier
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Comparaison */}
      <PenaltyComparison
        metrics={metrics}
        simulation={simulation}
        delayDays={delayDays}
        existingDelayAmount={existingDelay?.montant || 0}
        actualLaborCost={existingDelay && !isEditMode ? delayCostFromAffectations : undefined}
      />

      {/* Actions - juste après la comparaison */}
      <Card>
        <CardHeader>
          <CardTitle>⚡ Actions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3">
            {/* Bouton Modifier - visible uniquement si retard verrouillé */}
            {isLocked && (
              <Button
                onClick={() => setIsEditMode(true)}
                className="bg-primary border border-black text-white hover:bg-primary/90"
              >
                <Pencil className="mr-2 h-4 w-4" />
                Modifier le retard
              </Button>
            )}

            {/* Bouton Enregistrer/Mettre à jour - visible uniquement si pas verrouillé */}
            {!isLocked && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      onClick={() => {
                        // Si l'utilisateur a déjà confirmé sa sélection de jours (via Modal 1),
                        // on passe directement à la confirmation d'enregistrement
                        if (hasUserConfirmedDays && selectedWorkDays.size > 0) {
                          setShowConfirmModal(true);
                          return;
                        }

                        // Sinon, afficher le modal de sélection des jours avant enregistrement
                        // Initialiser les jours sélectionnés par défaut AVANT d'ouvrir le modal
                        if (contractEndDateLocal && selectedEndDate && delayDays > 0) {
                          const allDays = eachDayOfInterval({ start: addDays(contractEndDateLocal, 1), end: selectedEndDate });
                          const businessDays = allDays.filter(day => {
                            const dayOfWeek = day.getDay();
                            return dayOfWeek !== 0 && dayOfWeek !== 6;
                          });
                          if (businessDays.length > 0) {
                            const newMap = new Map<string, number>();
                            const halfDaysToSelect = Math.round(delayDays * 2);
                            let halfDaysSelected = 0;
                            // Remplir depuis le DERNIER jour ouvré vers le premier
                            for (let i = businessDays.length - 1; i >= 0 && halfDaysSelected < halfDaysToSelect; i--) {
                              const day = businessDays[i];
                              const dateKey = format(day, 'yyyy-MM-dd');
                              if (halfDaysSelected < halfDaysToSelect) {
                                newMap.set(`${dateKey}:PM`, 0.5);
                                halfDaysSelected++;
                              }
                              if (halfDaysSelected < halfDaysToSelect) {
                                newMap.set(`${dateKey}:AM`, 0.5);
                                halfDaysSelected++;
                              }
                            }

                            setSelectedWorkDays(newMap);
                          }
                        }
                        setPendingSave(true);
                        setShowDaysSelectionModal(true);
                      }}
                      disabled={saving || delayDays === 0}
                      variant={existingDelay ? "default" : "destructive"}
                    >
                      <Save className="mr-2 h-4 w-4" />
                      {saving
                        ? (existingDelay ? 'Mise à jour...' : 'Enregistrement...')
                        : (existingDelay
                            ? 'Mettre à jour le retard'
                            : (penaltyEnabled ? 'Enregistrer pénalité réelle' : 'Enregistrer retard')
                          )
                      }
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className={cn(
                    "max-w-xs",
                    existingDelay
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-destructive text-destructive-foreground border-destructive"
                  )}>
                    <div className="space-y-1">
                      <p className="font-semibold flex items-center gap-1">
                        <AlertTriangle className="h-4 w-4" />
                        {existingDelay ? 'Cette action va :' : 'Cette action va :'}
                      </p>
                      <ul className="text-sm list-disc list-inside space-y-0.5">
                        <li>{existingDelay ? 'Mettre à jour' : 'Créer'} un frais réel de <strong>{simulation.totalAdditionalCost.toFixed(2)}€</strong> sur ce chantier</li>
                        <li>Mettre à jour la rentabilité définitivement</li>
                      </ul>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {/* Bouton Annuler modifications - visible si en mode édition avec un retard existant */}
            {existingDelay && isEditMode && (
              <Button
                variant="ghost"
                onClick={() => setIsEditMode(false)}
              >
                Annuler
              </Button>
            )}

            {/* Bouton Supprimer - visible uniquement si retard enregistré */}
            {existingDelay && (
              <Button
                onClick={() => setShowDeleteModal(true)}
                disabled={deleting}
                className="bg-destructive border border-black text-black hover:bg-destructive/90"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {deleting ? 'Suppression...' : 'Supprimer'}
              </Button>
            )}

            {/* Bouton Générer rapport */}
            <Button
              variant="outline"
              disabled={delayDays === 0 || generatingPdf}
              onClick={handleGenerateReport}
            >
              {generatingPdf ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <FileText className="mr-2 h-4 w-4" />
              )}
              {generatingPdf ? 'Génération...' : 'Générer rapport'}
            </Button>
          </div>

          {/* Texte d'information */}
          <p className="text-xs text-muted-foreground flex items-start gap-1.5">
            <Info className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
            <span>
              {isLocked ? (
                <>Cliquez sur <strong>Modifier le retard</strong> pour éditer les paramètres. <strong>Supprimer</strong> retire le retard enregistré.</>
              ) : existingDelay ? (
                <><strong>Mettre à jour</strong> modifiera le retard existant. <strong>Annuler</strong> revient à l'affichage figé.</>
              ) : (
                <><strong>Enregistrer</strong> créera un frais réel impactant la rentabilité du chantier. <strong>Générer rapport</strong> produit un document sans modifier les données.</>
              )}
            </span>
          </p>
        </CardContent>
      </Card>

      {/* Section Point Critique (mode simulation) ou Bilan du Retard (mode post-enregistrement) */}
      <PointCritiqueSection
        joursSimules={delayDays}
        pointCritique={simulationForPointCritique.criticalDelayDays}
        isDelayRegistered={existingDelay !== null && !isEditMode}
        // Pour le bilan post-enregistrement : utiliser les métriques ACTUELLES (pas la simulation)
        // car les métriques actuelles reflètent déjà l'état réel avec le retard enregistré
        coutTotalRetard={existingDelay ? existingDelay.montant + delayCostFromAffectations : simulation.totalAdditionalCost}
        margeFinaleAvecRetard={existingDelay && !isEditMode ? (metrics?.marge_finale ?? 0) : simulation.finalMarginWithDelay}
        rentabiliteAvecRetard={existingDelay && !isEditMode ? (metrics?.marge_finale_pct ?? 0) : simulation.finalProfitabilityWithDelay}
        // Jours restants post-retard = Marge finale actuelle / Coût journalier équipe mobilisée pour le retard
        joursRestantsAvantNouveauDeficit={
          existingDelay && !isEditMode
            ? (coutJournalierEquipeRetard > 0 ? Math.floor((metrics?.marge_finale ?? 0) / coutJournalierEquipeRetard) : 0)
            : Math.max(0, simulationForPointCritique.criticalDelayDays - delayDays)
        }
      />

      {/* Alertes & Recommandations */}
      <Card>
        <CardHeader>
          <CardTitle>🚨 Alertes & Recommandations</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {simulation.recommendations.map((rec, idx) => (
            <Alert key={idx} variant={rec.includes('🚨') || rec.includes('⚠️') ? 'destructive' : 'default'}>
              <AlertDescription>{rec}</AlertDescription>
            </Alert>
          ))}

          {simulation.criticalDelayDays < 100 && (
            <Alert>
              <AlertDescription>
                <strong>Point critique :</strong> Au-delà de {simulation.criticalDelayDays} jours de retard,
                le projet devient déficitaire.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Modal de confirmation avant enregistrement */}
      <AlertDialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Confirmer l'enregistrement
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  Vous êtes sur le point d'enregistrer un <strong>{penaltyEnabled ? 'frais de pénalité' : 'coût de retard'}</strong> de :
                </p>
                <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-destructive">{simulation.totalAdditionalCost.toFixed(2)} €</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    pour {delayDays} jour{delayDays > 1 ? 's' : ''} de retard
                  </p>
                </div>
                <p className="text-sm text-muted-foreground">
                  Ce coût sera ajouté aux frais du chantier. Vous pourrez le modifier ou le supprimer ultérieurement.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={existingDelay ? handleUpdateDelay : handleSavePenalty}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              <Save className="mr-2 h-4 w-4" />
              {existingDelay ? 'Confirmer la modification' : 'Confirmer l\'enregistrement'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal de confirmation de suppression */}
      <AlertDialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="h-5 w-5" />
              Supprimer le retard
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  Vous êtes sur le point de supprimer le retard enregistré :
                </p>
                {existingDelay && (
                  <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-destructive">
                      {(delayCostFromAffectations + (penaltyEnabled ? simulation.penaltyAmount : 0)).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {existingDelay.description}
                    </p>
                  </div>
                )}
                <p className="text-sm">
                  Cette action supprimera le frais de retard et mettra à jour la rentabilité du chantier.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteDelay}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Confirmer la suppression
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal de résolution des conflits de retard */}
      <AlertDialog open={showConflitRetardModal} onOpenChange={setShowConflitRetardModal}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-orange-500">
              <AlertTriangle className="h-5 w-5" />
              Conflit d'affectation détecté
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  <strong>{membresEnConflit.length}</strong> membre(s) sont déjà affectés sur un autre chantier pendant la période de retard.
                </p>
                <p className="text-sm text-muted-foreground">
                  Choisissez comment résoudre ce conflit pour éviter le double comptage des coûts.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>

          {/* Liste des conflits */}
          <div className="space-y-2 my-4 max-h-40 overflow-y-auto">
            {conflitsRetard.map((conflit, i) => (
              <div key={i} className="flex items-center gap-2 p-2 bg-orange-500/10 rounded text-sm">
                <User className="h-4 w-4 text-orange-500 flex-shrink-0" />
                <span className="font-medium">{conflit.membre.prenom} {conflit.membre.nom}</span>
                <span className="text-muted-foreground">
                  → {conflit.chantierNom} ({format(new Date(conflit.dateDebut), 'dd/MM', { locale: fr })} - {format(new Date(conflit.dateFin), 'dd/MM', { locale: fr })})
                </span>
              </div>
            ))}
          </div>

          {/* 4 Options de résolution */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Option A: Équipe partielle */}
            {membresDisponibles.length > 0 && (
              <Button
                variant="outline"
                className="h-auto py-3 flex flex-col items-start border-green-500/50 hover:bg-green-500/10"
                onClick={handleRetardEquipePartielle}
                disabled={resolvingConflict}
              >
                <div className="flex items-center gap-2 text-green-500">
                  <Users className="h-4 w-4" />
                  <span className="font-medium">Équipe partielle</span>
                </div>
                <span className="text-xs text-muted-foreground text-left">
                  Affecter uniquement les {membresDisponibles.length} membre(s) disponible(s)
                </span>
              </Button>
            )}

            {/* Option B: Rotation */}
            <Button
              variant="outline"
              className="h-auto py-3 flex flex-col items-start border-blue-500/50 hover:bg-blue-500/10"
              onClick={handleRetardRotation}
              disabled={resolvingConflict}
            >
              <div className="flex items-center gap-2 text-blue-500">
                <RefreshCw className="h-4 w-4" />
                <span className="font-medium">Rotation</span>
              </div>
              <span className="text-xs text-muted-foreground text-left">
                Retirer des autres chantiers et affecter au retard
              </span>
            </Button>

            {/* Option C: Reporter */}
            <Button
              variant="outline"
              className="h-auto py-3 flex flex-col items-start border-purple-500/50 hover:bg-purple-500/10"
              onClick={handleRetardReporter}
              disabled={resolvingConflict}
            >
              <div className="flex items-center gap-2 text-purple-500">
                <CalendarIcon className="h-4 w-4" />
                <span className="font-medium">Reporter le retard</span>
              </div>
              <span className="text-xs text-muted-foreground text-left">
                Décaler les dates après libération des membres
              </span>
            </Button>

            {/* Option D: Progressif */}
            {membresDisponibles.length > 0 && (
              <Button
                variant="outline"
                className="h-auto py-3 flex flex-col items-start border-amber-500/50 hover:bg-amber-500/10"
                onClick={handleRetardProgressif}
                disabled={resolvingConflict}
              >
                <div className="flex items-center gap-2 text-amber-500">
                  <Clock className="h-4 w-4" />
                  <span className="font-medium">Progressif</span>
                </div>
                <span className="text-xs text-muted-foreground text-left">
                  {membresDisponibles.length} maintenant, {membresEnConflit.length} rejoignent après
                </span>
              </Button>
            )}
          </div>

          {resolvingConflict && (
            <div className="flex items-center justify-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Résolution en cours...</span>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel disabled={resolvingConflict}>Annuler</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal de sélection des jours travaillés */}
      <AlertDialog open={showDaysSelectionModal} onOpenChange={setShowDaysSelectionModal}>
        <AlertDialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-primary" />
              Sélectionner les jours travaillés
            </AlertDialogTitle>
            <AlertDialogDescription>
              La période sélectionnée contient plus de jours travaillés que prévu.
              Cochez les jours où l'équipe sera mobilisée.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="py-4">
            {/* Info période */}
            <div className="flex justify-between items-center mb-4 p-3 bg-muted/50 rounded-lg">
              <div>
                <p className="text-sm text-muted-foreground">Période de retard</p>
                <p className="font-semibold">
                  {contractEndDateLocal && format(addDays(contractEndDateLocal, 1), 'dd MMM', { locale: fr })}
                  {' → '}
                  {selectedEndDate && format(selectedEndDate, 'dd MMM yyyy', { locale: fr })}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Jours sélectionnés</p>
                <p className="text-2xl font-bold text-primary">{totalSelectedDays}</p>
              </div>
            </div>

            {/* Instruction */}
            <p className="text-xs text-muted-foreground text-center mb-3">
              Cliquez sur <strong>M</strong> (matin) ou <strong>A</strong> (après-midi) pour une demi-journée, ou sur le <strong>numéro</strong> pour un jour complet
            </p>

            {/* Grille des jours avec demi-journées */}
            <div className="space-y-2">
              {/* En-tête avec légende AM/PM */}
              <div className="grid grid-cols-7 gap-1.5 mb-2">
                {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(jour => (
                  <div key={jour} className="text-center text-xs font-medium text-muted-foreground">
                    {jour}
                  </div>
                ))}
              </div>

              {/* Décalage pour aligner avec le bon jour de la semaine */}
              <div className="grid grid-cols-7 gap-1.5">
                {daysInDelayPeriod.length > 0 && (
                  <>
                    {Array.from({ length: (daysInDelayPeriod[0].getDay() + 6) % 7 }).map((_, i) => (
                      <div key={`empty-${i}`} />
                    ))}
                  </>
                )}

                {/* Jours avec deux demi-journées cliquables */}
                {daysInDelayPeriod.map(day => {
                  const isWeekendDay = isWeekend(day);
                  const amSelected = isHalfDaySelected(day, 'AM');
                  const pmSelected = isHalfDaySelected(day, 'PM');
                  const isFullySelected = amSelected && pmSelected;

                  return (
                    <div
                      key={day.toISOString()}
                      className={cn(
                        "rounded-lg overflow-hidden border transition-all",
                        isWeekendDay
                          ? "border-orange-500/30 bg-orange-950/10"
                          : "border-border bg-card",
                        isFullySelected && "ring-2 ring-primary ring-offset-1 ring-offset-background"
                      )}
                    >
                      {/* Numéro du jour - cliquable pour tout sélectionner/désélectionner */}
                      <button
                        onClick={() => toggleFullDay(day)}
                        className={cn(
                          "w-full text-center py-1 text-sm font-bold transition-colors",
                          isFullySelected
                            ? isWeekendDay ? "bg-orange-500 text-white" : "bg-primary text-primary-foreground"
                            : "hover:bg-muted"
                        )}
                      >
                        {format(day, 'd')}
                      </button>

                      {/* Conteneur des deux demi-journées */}
                      <div className="flex">
                        {/* Matin */}
                        <button
                          onClick={() => toggleHalfDay(day, 'AM')}
                          className={cn(
                            "flex-1 py-1.5 text-[10px] font-medium transition-all border-r border-border/50",
                            amSelected
                              ? isWeekendDay
                                ? "bg-orange-500 text-white"
                                : "bg-primary text-primary-foreground"
                              : "hover:bg-muted/80 text-muted-foreground"
                          )}
                        >
                          M
                        </button>

                        {/* Après-midi */}
                        <button
                          onClick={() => toggleHalfDay(day, 'PM')}
                          className={cn(
                            "flex-1 py-1.5 text-[10px] font-medium transition-all",
                            pmSelected
                              ? isWeekendDay
                                ? "bg-orange-500 text-white"
                                : "bg-primary text-primary-foreground"
                              : "hover:bg-muted/80 text-muted-foreground"
                          )}
                        >
                          A
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Légende */}
            <div className="flex flex-wrap gap-4 mt-4 text-xs text-muted-foreground justify-center">
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 rounded bg-primary" />
                <span>Sélectionné</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 rounded bg-orange-500" />
                <span>Weekend travaillé</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 rounded bg-muted border border-border" />
                <span>Non travaillé</span>
              </div>
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelDaysSelection}>
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmWorkDays}
              disabled={totalSelectedDays === 0}
            >
              Confirmer ({totalSelectedDays} jour{totalSelectedDays > 1 ? 's' : ''})
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal de sélection équipe */}
      <ModalSelectionEquipeRetard
        isOpen={modalEquipeOpen}
        onClose={() => {
          setModalEquipeOpen(false);
          // Si aucun membre sélectionné et qu'on ferme, revenir au choix initial
          if (selectedMembersForDelay.length === 0 && teamChoice === 'custom') {
            setTeamChoice(null);
          }
        }}
        entrepriseId={entrepriseId}
        chantierId={chantierId}
        delayDays={delayDays}
        dateDebutRetard={realStartDate || (simulation.contractEndDate ? addDays(simulation.contractEndDate, 1) : undefined)}
        dateFinRetard={realEndDate || simulation.newEndDate || (simulation.contractEndDate ? addBusinessDays(simulation.contractEndDate, Math.ceil(delayDays)) : undefined)}
        selectedMembers={selectedMembersForDelay}
        onChange={(members) => setSelectedMembersForDelay(members)}
        onConfirm={(members) => {
          setSelectedMembersForDelay(members);
          setTeamChoice('custom');
          setModalEquipeOpen(false);
        }}
      />

      {/* Tips contextuel au premier accès */}
      <TipsModal isOpen={showTips} onClose={handleCloseTips} title="Simulateur de Retard">
        <div className="space-y-4">
          <div className="bg-amber-500/20 border border-amber-500/50 rounded-lg p-3">
            <p className="text-amber-200 text-sm flex items-center gap-2">
              <span>🧪</span>
              <span>C'est une <strong>SIMULATION</strong>. Rien n'est enregistré tant que vous ne cliquez pas sur "Enregistrer le retard".</span>
            </p>
          </div>

          <p>Cet outil vous permet d'<strong>anticiper</strong> l'impact financier d'un retard avant qu'il ne se produise.</p>

          <ol className="space-y-3">
            <li className="flex gap-3">
              <span className="font-bold text-primary">1.</span>
              <span>Activez ou désactivez les pénalités selon votre contrat</span>
            </li>
            <li className="flex gap-3">
              <span className="font-bold text-primary">2.</span>
              <span>Choisissez le type de pénalité : pourcentage, montant fixe, ou paliers CCAG</span>
            </li>
            <li className="flex gap-3">
              <span className="font-bold text-primary">3.</span>
              <span>Utilisez le slider pour définir le nombre de jours travaillés pendant le retard</span>
            </li>
            <li className="flex gap-3">
              <span className="font-bold text-primary">4.</span>
              <span>Sélectionnez l'équipe mobilisée : <strong>"Équipe actuelle"</strong> ou <strong>"Équipe personnalisée"</strong></span>
            </li>
            <li className="flex gap-3">
              <span className="font-bold text-primary">5.</span>
              <span>Consultez le <strong>"Bilan du retard"</strong> qui résume l'impact financier et le nombre de jours avant déficit</span>
            </li>
            <li className="flex gap-3">
              <span className="font-bold text-primary">6.</span>
              <span>Si le retard est confirmé, cliquez sur "Enregistrer" pour l'officialiser dans les comptes du chantier</span>
            </li>
          </ol>
        </div>
      </TipsModal>
    </div>
  );
}
